import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    MessageCircle,
    CheckCircle2,
    XCircle,
    Clock,
    Send,
    User,
    Tag,
    Calendar,
    Loader2,
    Eye,
    Download,
    X,
    File,
    Paperclip,
    Image as ImageIcon,
    Reply,
    ChevronDown,
    ChevronUp,
    Search,
    Mic,
    Volume2
} from 'lucide-react';
import VoiceRecorder from '../../components/Chat/VoiceRecorder';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import {
    connectSupportSocket,
    disconnectSupportSocket,
    getSupportSocket,
    isSupportSocketConnected,
    adminSocketEvents
} from '../../services/supportSocketService';
import { selectAdminList } from '../../redux/adminSlice';
import { getCategories, getAllSupportSubCategories } from '../../redux/masterSlice';

import getFileURL from '../../utils/fileURL';
import MasterLabel from '../../components/common/MasterLabel';

// Helper: find a master category/subcategory ID by name from the Redux-loaded lists.
// This is the safety net that makes translation work even when the backend API
// response doesn't include `categoryId` / `subCategoryId` on the ticket (e.g. for
// legacy closed tickets whose DB documents predate the projection fix).
const resolveSupportIds = (ticket, masterCategories, allSubCategories) => {
    let categoryId = ticket?.categoryId || null;
    let subCategoryId = ticket?.subCategoryId || null;
    if (categoryId && subCategoryId) return { categoryId, subCategoryId };

    const categoryNameGuess = ticket?.categoryName || ticket?.category || null;
    // `ticket.subject` is stored as the subcategory name snapshot at creation time,
    // so it's the most reliable name source when the explicit subCategory field is absent.
    const subCategoryNameGuess = ticket?.subCategoryName || ticket?.subCategory || ticket?.subject || null;

    if (!categoryId && categoryNameGuess && Array.isArray(masterCategories)) {
        const match = masterCategories.find(
            (c) => (c.name || '').toLowerCase() === `${categoryNameGuess}`.toLowerCase()
        );
        if (match) categoryId = match.categoryId || match.id;
    }
    if (!subCategoryId && subCategoryNameGuess && Array.isArray(allSubCategories)) {
        const match = allSubCategories.find(
            (s) => (s.name || '').toLowerCase() === `${subCategoryNameGuess}`.toLowerCase()
        );
        if (match) {
            subCategoryId = match.subCategoryId || match.id;
            if (!categoryId) categoryId = match.categoryId || null;
        }
    }
    return { categoryId, subCategoryId };
};

// Renders the ticket's category name with translation. Previously this was a
// "category / subcategory" breadcrumb but that duplicated the subject line above
// (which is the subcategory snapshot). Now it only shows the parent category.
const TicketCategoryLine = ({ ticket }) => {
    const masterCategories = useSelector((state) => state.master?.categories || []);
    const allSubCategories = useSelector((state) => state.master?.allSupportSubCategories || []);
    const { categoryId } = resolveSupportIds(ticket, masterCategories, allSubCategories);

    const hasCategory = ticket?.category || ticket?.categoryName || categoryId;
    if (!hasCategory) return null;

    return (
        <MasterLabel
            type="masterSupportCategory"
            id={categoryId}
            field="name"
            fallback={ticket.categoryName || ticket.category || ''}
        />
    );
};

// Translates the ticket `subject` field (which is the subcategory-name snapshot) by
// resolving a subCategoryId via direct field → master-list name lookup, then passing
// the ID to <MasterLabel>.
const TicketSubjectLabel = ({ ticket }) => {
    const masterCategories = useSelector((state) => state.master?.categories || []);
    const allSubCategories = useSelector((state) => state.master?.allSupportSubCategories || []);
    const { subCategoryId } = resolveSupportIds(ticket, masterCategories, allSubCategories);
    return (
        <MasterLabel
            type="masterSupportSubCategory"
            id={subCategoryId}
            field="name"
            fallback={ticket.subject || ''}
        />
    );
};

// Debounce hook for admin search
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};


const MAX_SEARCH_LENGTH = 50;

// ---- Support SLA priority + live countdown ------------------------------
// Hours allotted to each frozen tier. 'Immediate' (Dedicated) gets the tightest
// window AND always ranks as the top group. Unknown tier → 48h (lowest).
const TIER_HOURS = { Immediate: 1, '12h': 12, '24h': 24, '48h': 48 };
const TIER_LABEL = { Immediate: 'Dedicated', '12h': '12h', '24h': '24h', '48h': '48h' };
const tierRank = (tier) => (tier === 'Immediate' ? 0 : 1);

// Time budget + remaining for a ticket relative to `now` (ms epoch).
const slaState = (ticket, now) => {
  const tier = ticket?.supportTier || '48h';
  const hours = TIER_HOURS[tier] ?? 48;
  const created = new Date(ticket?.createdAt || ticket?.created_at || now).getTime();
  const totalMs = hours * 3600 * 1000;
  const deadline = created + totalMs;
  const msLeft = deadline - now;
  return { tier, hours, totalMs, deadline, msLeft, overdue: msLeft < 0 };
};

// Priority: Dedicated group first, then least time-left first (overdue on top).
const sortByPriority = (list, now) =>
  [...(list || [])].sort((a, b) => {
    const sa = slaState(a, now), sb = slaState(b, now);
    const ra = tierRank(sa.tier), rb = tierRank(sb.tier);
    if (ra !== rb) return ra - rb;
    return sa.msLeft - sb.msLeft;
  });

// Compact largest-unit duration: "2d", "5h", "23m", "45s".
const fmtCompact = (ms) => {
  const s = Math.floor(Math.abs(ms) / 1000);
  if (s >= 86400) return `${Math.floor(s / 86400)}d`;
  if (s >= 3600) return `${Math.floor(s / 3600)}h`;
  if (s >= 60) return `${Math.floor(s / 60)}m`;
  return `${s}s`;
};
// Verbose duration for tooltips: "5h 02m", "2d 4h".
const fmtDur = (ms) => {
  const s = Math.floor(Math.abs(ms) / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
};

// Circular SLA countdown ring. Self-ticking every 1s so it counts down (and up,
// once overdue) live without re-rendering the parent. Green → amber → red by
// fraction of time left; full red + "Overdue" past the deadline.
const SlaRing = ({ ticket, size = 46, stroke = 4 }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const { tier, totalMs, msLeft, overdue } = slaState(ticket, now);
  const fracLeft = overdue ? 0 : Math.max(0, Math.min(1, msLeft / totalMs));
  const color = overdue ? '#ef4444' : fracLeft > 0.5 ? '#10b981' : fracLeft > 0.2 ? '#f59e0b' : '#ef4444';
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (overdue ? 1 : fracLeft));
  const label = TIER_LABEL[tier] || tier;
  return (
    <div
      data-testid="sla-ring"
      className="flex flex-col items-center justify-center flex-shrink-0"
      title={`${label} SLA · ${overdue ? `overdue by ${fmtDur(msLeft)}` : `${fmtDur(msLeft)} left`}`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s linear, stroke 0.6s linear' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] font-extrabold leading-none" style={{ color }}>{fmtCompact(msLeft)}</span>
        </div>
      </div>
      <span className="mt-0.5 text-[9px] font-semibold leading-none" style={{ color: overdue ? '#ef4444' : '#6b7280' }}>
        {overdue ? 'Overdue' : 'left'}
      </span>
    </div>
  );
};

const AdminSupport = () => {
    const { t } = useTranslation(['support']);
    const location = useLocation();
    const dispatch = useDispatch();
    const { adminList, adminListLoading } = useSelector((state) => state.admin);
    const [activeTab, setActiveTab] = useState('unassigned');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [messageInput, setMessageInput] = useState('');
    const [messageError, setMessageError] = useState('');
    const [tickets, setTickets] = useState({
        unassignedTickets: [],
        myTickets: [],
        closedTickets: []
    });
    // SLA sub-tab filter (Unassigned / My Tickets) — by the ticket's frozen tier.
    const [tierFilter, setTierFilter] = useState('all');
    const SLA_TABS = [
        { id: 'all', label: 'All' },
        { id: 'Immediate', label: 'Dedicated' },
        { id: '12h', label: '12h' },
        { id: '24h', label: '24h' },
        { id: '48h', label: '48h' },
    ];
    const byTier = (list) => (tierFilter === 'all' ? (list || []) : (list || []).filter((tk) => (tk.supportTier || '48h') === tierFilter));
    // Re-sort the SLA lists periodically so priority reflects elapsed time (rings
    // self-tick every second; 30s is plenty for re-ordering the cards).
    const [, setSlaTick] = useState(0);
    useEffect(() => {
        const i = setInterval(() => setSlaTick((n) => n + 1), 30000);
        return () => clearInterval(i);
    }, []);
    // Tier-filtered AND priority-sorted: Dedicated group first, then closest to its
    // own SLA breach (overdue on top).
    const prioritized = (list) => sortByPriority(byTier(list), Date.now());
    const [isLoading, setIsLoading] = useState(true);
    const messagesEndRef = useRef(null);

    // Pre-load master categories + flat subcategory list once on mount so the
    // TicketCategoryLine / TicketSubjectLabel helpers can fall back to name-based
    // lookups when the ticket API response doesn't include categoryId/subCategoryId
    // (e.g. legacy closed tickets whose DB docs predate the backend projection fix).
    useEffect(() => {
        dispatch(getCategories());
        dispatch(getAllSupportSubCategories());
    }, [dispatch]);
    const fileInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    // Admin select dropdown state (per ticket)
    const [openAdminDropdownId, setOpenAdminDropdownId] = useState(null);
    const [selectedAdmins, setSelectedAdmins] = useState({}); // { ticketId: { id, name } }
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [adminSearchError, setAdminSearchError] = useState('');
    const adminDropdownRef = useRef(null);
    const adminSearchInputRef = useRef(null);
    const debouncedAdminSearch = useDebounce(adminSearchQuery.trim(), 400);

    // Reassign dropdown state (for myTickets chat header)
    const [reassignDropdownOpen, setReassignDropdownOpen] = useState(false);
    const [reassignSearchQuery, setReassignSearchQuery] = useState('');
    const [reassignSearchError, setReassignSearchError] = useState('');
    const reassignDropdownRef = useRef(null);
    const debouncedReassignSearch = useDebounce(reassignSearchQuery.trim(), 400);

    const handleScroll = () => {
        if (!chatAreaRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollBottom(!isNearBottom);
    };

    console.log('🔵 AdminSupport component mounting/rendering');
    console.log('🔌 Socket connected:', isSupportSocketConnected());
    const chatAreaRef = useRef(null);
    const loadingTimeoutRef = useRef(null);
    const listenersAttached = useRef(false);
    const visibilityListenerRef = useRef(null);
    const setupInProgress = useRef(false);
    const disconnectTimeoutRef = useRef(null);
    const socketRetryCount = useRef(0);
    const MAX_SOCKET_RETRIES = 20; // Maximum 20 retries (1 second total)

    // Connect socket when support page is opened, disconnect when leaving
    useEffect(() => {
        console.log('🔌 Admin support page opened - connecting socket...');

        // Clear any pending disconnect
        if (disconnectTimeoutRef.current) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
        }

        const adminId = localStorage.getItem('ID');
        if (!adminId) {
            console.error('❌ Admin ID not found');
            return;
        }

        // Connect socket when page opens
        try {
            console.log('📡 Connecting support socket for admin:', adminId);
            const socketInstance = connectSupportSocket({ role: 'admin', id: adminId });
            if (!socketInstance) {
                console.error('❌ Failed to create socket instance. Check authentication and configuration.');
                toast.warning(t('support.failed_initialize_support'));
            } else {
                console.log('✅ Socket instance created successfully:', socketInstance.id || 'connecting...');
            }
        } catch (error) {
            console.error('❌ Error connecting socket:', error);
            toast.warning(t('support.failed_connect_support'));
        }

        // Cleanup: Disconnect socket when leaving the page
        return () => {
            console.log('🚪 Leaving admin support page - scheduling socket disconnect...');
            // Delay disconnect to handle React StrictMode double-mounting
            // Only disconnect if component doesn't remount quickly
            disconnectTimeoutRef.current = setTimeout(() => {
                console.log('✅ Disconnecting support socket (component unmounted)');
                disconnectSupportSocket();
                disconnectTimeoutRef.current = null;
            }, 500); // Wait 500ms to see if component remounts
        };
    }, []); // Run once on mount

    // Helper function to normalize ticket data from backend
    const normalizeTicket = (ticket) => {
        const normalized = {
            ...ticket,
            category: ticket.category || ticket.categoryName || ticket.categoryId,
            subCategory: ticket.subCategory || ticket.subCategoryName || ticket.subCategoryId,
            userName: ticket.userName || ticket.userRole || t('support.unknown_user')
        };
        console.log('Normalizing ticket:', {
            original: {
                category: ticket.category,
                categoryName: ticket.categoryName,
                subCategory: ticket.subCategory,
                subCategoryName: ticket.subCategoryName
            },
            normalized: {
                category: normalized.category,
                subCategory: normalized.subCategory
            }
        });
        return normalized;
    };

    // Setup socket event listeners (socket connection is managed globally)
    useEffect(() => {
        console.log('🔄 AdminSupport useEffect RUNNING - Setting up socket event listeners');
        console.log('📊 Listeners already attached:', listenersAttached.current);

        const adminId = localStorage.getItem('ID');
        if (!adminId) {
            toast.warning(t('support.admin_id_not_found'));
            return;
        }

        // Reset loading state when component mounts
        setIsLoading(true);

        // Always clean up old listeners before setting up new ones
        console.log('🧹 Cleaning up old listeners before setup');
        adminSocketEvents.removeListener('unassignedTickets');
        adminSocketEvents.removeListener('adminTickets');
        adminSocketEvents.removeListener('closedTickets');
        adminSocketEvents.removeListener('newSupportTicket');
        adminSocketEvents.removeListener('adminTicketListUpdate');
        adminSocketEvents.removeListener('ticketAccepted');
        adminSocketEvents.removeListener('ticketAlreadyAssigned');
        adminSocketEvents.removeListener('supportTicketJoined');
        adminSocketEvents.removeListener('supportHistory');
        adminSocketEvents.removeListener('receiveSupportMessage');
        adminSocketEvents.removeListener('supportTicketClosed');
        adminSocketEvents.removeListener('ticketReassignmentSuccess');
        adminSocketEvents.removeListener('ticketReassigned');

        // Setup Socket.IO event listeners
        // CRITICAL: According to guide, backend automatically emits unassignedTickets, adminTickets, and closedTickets on connection
        // So we MUST attach listeners BEFORE socket connects to catch the automatic emissions
        const setupListenersAndRequestTickets = () => {
            // Prevent duplicate setup
            if (listenersAttached.current) {
                console.log('⏸️ Listeners already attached, skipping...');
                return;
            }

            // Wait for socket instance to exist (even if not connected yet)
            let socket = getSupportSocket();

            // If socket doesn't exist, try to create it
            if (!socket) {
                console.log('⚠️ Socket instance not found, attempting to create it...');
                const adminId = localStorage.getItem('ID');
                if (adminId) {
                    try {
                        socket = connectSupportSocket({ role: 'admin', id: adminId });
                        if (!socket) {
                            console.error('❌ Failed to create socket instance. Check authentication.');
                            socketRetryCount.current += 1;
                            if (socketRetryCount.current >= MAX_SOCKET_RETRIES) {
                                console.error('❌ Socket instance not created after maximum retries. Please refresh the page.');
                                toast.warning(t('support.failed_to_initialize', 'Failed to initialize support service. Please refresh the page.'));
                                setIsLoading(false);
                                return;
                            }
                            // Only log every 5th retry to reduce console spam
                            if (socketRetryCount.current % 5 === 0) {
                                console.log(`⏳ Socket instance not created yet, waiting... (attempt ${socketRetryCount.current}/${MAX_SOCKET_RETRIES})`);
                            }
                            setTimeout(setupListenersAndRequestTickets, 100); // Retry after 100ms
                            return;
                        }
                    } catch (error) {
                        console.error('❌ Error creating socket:', error);
                        socketRetryCount.current += 1;
                        if (socketRetryCount.current >= MAX_SOCKET_RETRIES) {
                            console.error('❌ Socket instance not created after maximum retries. Please refresh the page.');
                            toast.warning(t('support.failed_to_initialize', 'Failed to initialize support service. Please refresh the page.'));
                            setIsLoading(false);
                            return;
                        }
                        setTimeout(setupListenersAndRequestTickets, 100);
                        return;
                    }
                } else {
                    console.error('❌ Admin ID not found, cannot create socket');
                    toast.warning(t('support.admin_id_not_found'));
                    setIsLoading(false);
                    return;
                }
            }

            // Verify socket instance exists
            if (!socket) {
                socketRetryCount.current += 1;
                if (socketRetryCount.current >= MAX_SOCKET_RETRIES) {
                    console.error('❌ Socket instance not created after maximum retries. Please refresh the page.');
                    toast.warning(t('support.failed_to_initialize', 'Failed to initialize support service. Please refresh the page.'));
                    setIsLoading(false);
                    return;
                }
                // Only log every 5th retry to reduce console spam
                if (socketRetryCount.current % 5 === 0) {
                    console.log(`⏳ Socket instance not created yet, waiting... (attempt ${socketRetryCount.current}/${MAX_SOCKET_RETRIES})`);
                }
                setTimeout(setupListenersAndRequestTickets, 100); // Retry after 100ms
                return;
            }

            // Reset retry count on success
            socketRetryCount.current = 0;

            console.log('🎧 Setting up listeners (socket instance exists, will catch automatic emissions on connect)...');
            console.log('   Socket connected:', socket.connected);
            console.log('   Socket connecting:', socket.connecting);

            // CRITICAL: If socket is already connected, we might have missed automatic emissions
            // But we still attach listeners for future events and request tickets manually
            if (socket.connected) {
                console.warn('⚠️ Socket already connected - might have missed automatic emissions, will request manually');
            }

            setupInProgress.current = true;
            listenersAttached.current = true;

            try {
                // CRITICAL: Attach listeners BEFORE socket connects
                // Backend automatically emits these events when socket connects
                // If we attach after connection, we miss the initial data

                // Listen for initial ticket lists
                // Backend sends arrays directly, not wrapped in an object
                console.log('🎧 Attaching unassignedTickets listener (before connection check)...');
                const socketCheck = getSupportSocket();
                console.log('   Socket exists:', !!socketCheck);
                console.log('   Socket connected:', socketCheck?.connected);
                adminSocketEvents.onUnassignedTickets((data) => {
                    console.log('📥 Received unassigned tickets:', data);
                    if (data === null || data === undefined) {
                        console.warn('⚠️ Received null/undefined unassigned tickets, setting to empty array');
                        setTickets(prev => ({
                            ...prev,
                            unassignedTickets: []
                        }));
                        setIsLoading(false);
                        return;
                    }
                    if (data && data.length > 0) {
                        console.log('First ticket structure:', data[0]);
                        console.log('Available fields:', Object.keys(data[0]));
                    }
                    const normalizedTickets = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    console.log('✅ Normalized unassigned tickets count:', normalizedTickets.length);
                    setTickets(prev => ({
                        ...prev,
                        unassignedTickets: normalizedTickets
                    }));
                    setIsLoading(false);
                });

                console.log('🎧 Attaching onAdminTickets listener...');
                const socketCheck2 = getSupportSocket();
                console.log('   Socket exists:', !!socketCheck2);
                console.log('   Socket connected:', socketCheck2?.connected);
                adminSocketEvents.onAdminTickets((data) => {
                    console.log('📥 ===== RECEIVED ADMIN TICKETS (LISTENER FIRED) =====');
                    console.log('📥 Received admin tickets data:', data);
                    console.log('📥 Data type:', typeof data, 'Is array:', Array.isArray(data));
                    if (data === null || data === undefined) {
                        console.warn('⚠️ Received null/undefined admin tickets, setting to empty array');
                        setTickets(prev => ({
                            ...prev,
                            myTickets: []
                        }));
                        setIsLoading(false);
                        console.log('📥 ===== END ADMIN TICKETS =====');
                        return;
                    }
                    if (data && data.length > 0) {
                        console.log('First admin ticket structure:', data[0]);
                        console.log('Available fields:', Object.keys(data[0]));
                    } else {
                        console.log('📭 Received empty admin tickets array');
                    }
                    const normalizedTickets = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    console.log('✅ Normalized admin tickets count:', normalizedTickets.length);
                    console.log('✅ Setting myTickets to:', normalizedTickets);
                    setTickets(prev => {
                        const newState = {
                            ...prev,
                            myTickets: normalizedTickets
                        };
                        console.log('📊 Updated tickets state:', {
                            unassigned: newState.unassignedTickets.length,
                            myTickets: newState.myTickets.length,
                            closed: newState.closedTickets.length
                        });
                        return newState;
                    });
                    setIsLoading(false);
                    console.log('📥 ===== END ADMIN TICKETS =====');
                });

                console.log('🎧 Attaching onClosedTickets listener...');
                const socketCheck3 = getSupportSocket();
                console.log('   Socket exists:', !!socketCheck3);
                console.log('   Socket connected:', socketCheck3?.connected);
                adminSocketEvents.onClosedTickets((data) => {
                    console.log('📥 Received closed tickets:', data);
                    console.log('📥 Closed tickets data type:', typeof data, 'Is array:', Array.isArray(data));
                    if (data === null || data === undefined) {
                        console.warn('⚠️ Received null/undefined closed tickets, setting to empty array');
                        setTickets(prev => ({
                            ...prev,
                            closedTickets: []
                        }));
                        setIsLoading(false);
                        return;
                    }
                    if (data && data.length > 0) {
                        console.log('First closed ticket structure:', data[0]);
                    } else {
                        console.log('📭 Received empty closed tickets array');
                    }
                    const normalizedTickets = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    console.log('✅ Normalized closed tickets count:', normalizedTickets.length);
                    setTickets(prev => {
                        const newState = {
                            ...prev,
                            closedTickets: normalizedTickets
                        };
                        console.log('📊 Updated tickets state:', {
                            unassigned: newState.unassignedTickets.length,
                            myTickets: newState.myTickets.length,
                            closed: newState.closedTickets.length
                        });
                        return newState;
                    });
                    setIsLoading(false);
                });

                // IMPORTANT: According to guide, backend automatically emits tickets on connection
                // But we also request manually as fallback in case automatic emission was missed
                const requestTicketsIfNeeded = () => {
                    if (!isSupportSocketConnected()) {
                        console.log('⏳ Socket not connected yet, will request when connected...');
                        const socket = getSupportSocket();
                        if (socket) {
                            socket.once('connect', () => {
                                console.log('🔌 Socket connected, requesting tickets as fallback...');
                                adminSocketEvents.requestTickets();
                            });
                        }
                        return;
                    }

                    console.log('📤 Requesting admin tickets from backend (fallback if automatic emission missed)...');
                    console.log('   Admin ID:', adminId);
                    console.log('   Socket connected:', isSupportSocketConnected());

                    // Clear selected ticket when navigating back to force refresh
                    setSelectedTicket(null);

                    // Request tickets - backend should send unassignedTickets, adminTickets, and closedTickets
                    adminSocketEvents.requestTickets();

                    console.log('✅ Ticket request sent, waiting for responses...');
                };

                // Request immediately if socket already connected, otherwise wait for connect
                if (isSupportSocketConnected()) {
                    // Small delay to ensure listeners are fully attached
                    setTimeout(requestTicketsIfNeeded, 300);
                } else {
                    // Wait for socket to connect, then request
                    const socket = getSupportSocket();
                    if (socket) {
                        socket.once('connect', () => {
                            console.log('🔌 Socket connected, requesting tickets as fallback...');
                            setTimeout(requestTicketsIfNeeded, 300);
                        });
                    }
                }

                // Retry request after 3 seconds if no data received (in case automatic emission was missed)
                setTimeout(() => {
                    const hasData = tickets.unassignedTickets.length > 0 ||
                        tickets.myTickets.length > 0 ||
                        tickets.closedTickets.length > 0;
                    if (!hasData && isSupportSocketConnected()) {
                        console.log('🔄 No data received after 3 seconds, retrying ticket request...');
                        adminSocketEvents.requestTickets();
                    }
                }, 3000);

                // Fallback: Stop loading after timeout if no tickets received
                loadingTimeoutRef.current = setTimeout(() => {
                    console.log('⏱️ Loading timeout - stopping loading state');
                    console.log('   Final ticket counts:', {
                        unassigned: tickets.unassignedTickets.length,
                        myTickets: tickets.myTickets.length,
                        closed: tickets.closedTickets.length
                    });
                    setIsLoading(false);
                }, 3000); // 3 second timeout

                // Listen for NEW ticket created by doctor/patient
                adminSocketEvents.onNewSupportTicket((data) => {
                    console.log('New support ticket created:', data);

                    // Normalize and add to unassigned tickets list
                    const newTicket = normalizeTicket(data);

                    setTickets(prev => ({
                        ...prev,
                        unassignedTickets: [newTicket, ...prev.unassignedTickets]
                    }));
                });

                // Listen for ticket list updates (when ticket is assigned or closed)
                adminSocketEvents.onAdminTicketListUpdate((data) => {
                    console.log('📬 Admin ticket list updated:', data);

                    const { ticketId, action, assignedTo, closedAt } = data;

                    if (!ticketId) {
                        console.warn('⚠️ adminTicketListUpdate missing ticketId:', data);
                        return;
                    }

                    // Handle ticket assignment
                    if (action === 'assigned') {
                        console.log(`✅ Ticket ${ticketId} assigned to ${assignedTo}`);

                        // Remove from unassigned tickets
                        setTickets(prev => ({
                            ...prev,
                            unassignedTickets: prev.unassignedTickets.filter(t => t.ticketId !== ticketId)
                        }));

                        // If assigned to current admin, add to my tickets
                        const currentAdminId = localStorage.getItem('ID');
                        if (assignedTo === currentAdminId) {
                            // Request updated admin tickets to get the full ticket data
                            adminSocketEvents.requestTickets();
                        }

                    }

                    // Handle ticket reassignment
                    if (action === 'reassigned') {
                        console.log(`🔄 Ticket ${ticketId} reassigned to ${assignedTo}`);
                        const currentAdminId = localStorage.getItem('ID');

                        // If reassigned TO current admin, fetch fresh ticket list
                        if (assignedTo === currentAdminId) {
                            adminSocketEvents.requestTickets();
                        }

                        // If reassigned FROM current admin, remove from myTickets
                        setTickets(prev => {
                            const wasInMyTickets = prev.myTickets.some(t => t.ticketId === ticketId);
                            if (wasInMyTickets && assignedTo !== currentAdminId) {
                                return {
                                    ...prev,
                                    myTickets: prev.myTickets.filter(t => t.ticketId !== ticketId)
                                };
                            }
                            return prev;
                        });

                        // Clear selected ticket if it was reassigned away
                        setSelectedTicket(prev => {
                            if (prev?.ticketId === ticketId && assignedTo !== currentAdminId) {
                                return null;
                            }
                            return prev;
                        });
                    }

                    // Handle ticket closure
                    if (action === 'closed') {
                        console.log(`🔒 Ticket ${ticketId} closed at ${closedAt}`);

                        // Remove from unassigned tickets
                        setTickets(prev => ({
                            ...prev,
                            unassignedTickets: prev.unassignedTickets.filter(t => t.ticketId !== ticketId),
                            // Also remove from my tickets if it was assigned to this admin
                            myTickets: prev.myTickets.filter(t => t.ticketId !== ticketId)
                        }));

                        // Request updated closed tickets to add it there
                        adminSocketEvents.requestTickets();

                    }
                });

                // Listen for ticket accepted
                adminSocketEvents.onTicketAccepted((data) => {
                    console.log('Ticket accepted:', data);

                    const normalizedTicket = normalizeTicket(data.ticket);
                    const currentAdminId = localStorage.getItem('ID');
                    // data.adminId = the admin ID who the ticket was assigned to (from socket emit)
                    // data.ticket.assignedTo = may also contain the assigned admin ID
                    const assignedAdminId = data.adminId || data.ticket?.assignedTo || data.ticket?.assignedAdminId;
                    const isAssignedToMe = assignedAdminId === currentAdminId;
                    console.log('🎫 Ticket assigned to:', assignedAdminId, '| Current admin:', currentAdminId, '| isAssignedToMe:', isAssignedToMe);

                    // Always remove from unassigned list for all admins
                    setTickets(prev => {
                        const updated = {
                            ...prev,
                            unassignedTickets: prev.unassignedTickets.filter(t => t.ticketId !== data.ticket.ticketId),
                        };
                        // Only add to myTickets if this ticket is assigned to the current admin
                        if (isAssignedToMe) {
                            updated.myTickets = [normalizedTicket, ...prev.myTickets];
                        }
                        return updated;
                    });

                    if (isAssignedToMe) {
                        // Switch to My Tickets tab and select the ticket
                        setActiveTab('myTickets');
                        setSelectedTicket(normalizedTicket);

                        // CRITICAL: Join the ticket room to receive messages from client
                        if (currentAdminId && normalizedTicket.ticketId) {
                            console.log('🎫 Joining ticket room after acceptance:', normalizedTicket.ticketId);
                            adminSocketEvents.joinTicket(normalizedTicket.ticketId, currentAdminId, 'admin');
                        }
                    }
                });

                // Listen for already assigned error
                adminSocketEvents.onTicketAlreadyAssigned((data) => {
                    console.log('Ticket already assigned:', data);

                    // Remove from unassigned list
                    setTickets(prev => ({
                        ...prev,
                        unassignedTickets: prev.unassignedTickets.filter(t => t.ticketId !== data.ticketId)
                    }));
                });

                // Listen for ticket joined
                // Backend sends ticket summary with lastMessage, not full messages array
                adminSocketEvents.onTicketJoined((summary) => {
                    console.log('Ticket joined:', summary);
                    // Normalize the summary data and initialize with empty messages
                    const normalizedSummary = normalizeTicket(summary);
                    // Merge summary with previous data, but preserve status from prev if it exists
                    setSelectedTicket(prev => {
                        const merged = {
                            ...normalizedSummary,
                            ...prev,
                            ticketId: normalizedSummary.ticketId || prev?.ticketId,
                            messages: []
                        };
                        // If prev has a status, keep it; otherwise use summary status
                        if (prev?.status) {
                            merged.status = prev.status;
                        }
                        return merged;
                    });
                    // Fetch full message history
                    adminSocketEvents.fetchHistory(summary.ticketId, 1);
                });

                // Listen for message history
                adminSocketEvents.onHistory((data) => {
                    console.log('📜 Received message history:', data);
                    if (data.ticketId && data.messages) {
                        // Normalize and sort messages by timestamp
                        const normalizedMessages = data.messages.map(msg => ({
                            _id: msg._id,
                            messageId: msg.messageId,
                            senderRole: msg.senderRole,
                            message: msg.message,
                            time: msg.createdAt || new Date().toISOString(),
                            createdAt: msg.createdAt,
                            isRead: msg.isRead || false,
                            attachments: msg.attachments || [],
                            replyTo: msg.replyTo || null,
                            senderName: msg.senderName || null,
                            // Preserve system event payload (assignment / reassignment banners).
                            systemEvent: msg.systemEvent || null,
                        }));

                        // Sort by timestamp to ensure correct chronological order
                        const sortedMessages = normalizedMessages.sort((a, b) => {
                            const timeA = new Date(a.createdAt || a.time || 0).getTime();
                            const timeB = new Date(b.createdAt || b.time || 0).getTime();
                            return timeA - timeB;
                        });

                        setSelectedTicket(prev =>
                            prev?.ticketId === data.ticketId
                                ? { ...prev, messages: sortedMessages }
                                : prev
                        );

                        // Also propagate the history into the tickets list so that when a
                        // new `receiveSupportMessage` arrives and the handler syncs the
                        // selectedTicket from the list, the history isn't dropped.
                        setTickets(prev => {
                            const patchList = (list) =>
                                list.map((t) =>
                                    t.ticketId === data.ticketId
                                        ? { ...t, messages: sortedMessages }
                                        : t
                                );
                            return {
                                ...prev,
                                myTickets: patchList(prev.myTickets || []),
                                unassignedTickets: patchList(prev.unassignedTickets || []),
                                closedTickets: patchList(prev.closedTickets || []),
                            };
                        });
                    }
                });

                // Listen for new messages
                console.log('🎧 Attaching onReceiveMessage listener (Admin)...');
                adminSocketEvents.onReceiveMessage((data) => {
                    console.log('📬 ===== RECEIVED MESSAGE EVENT (ADMIN) =====');
                    console.log('📬 Full message data:', JSON.stringify(data, null, 2));
                    console.log('📬 Ticket ID:', data.ticketId);
                    console.log('📬 Selected ticket ID:', selectedTicket?.ticketId);

                    if (!data || !data.ticketId) {
                        console.error('❌ Invalid message data:', data);
                        return;
                    }

                    // Normalize message structure according to guide
                    // Backend sends: { _id, messageId, ticketId, senderId, senderRole, message, isRead, createdAt }
                    const normalizedMessage = {
                        _id: data._id,
                        messageId: data.messageId || `msg_${Date.now()}`,
                        senderRole: data.senderRole,
                        message: data.message,
                        time: data.createdAt || new Date().toISOString(),
                        createdAt: data.createdAt || new Date().toISOString(),
                        isRead: data.isRead || false,
                        attachments: data.attachments || [],
                        replyTo: data.replyTo || null,
                        senderName: data.senderName || null
                    };

                    console.log('📬 Normalized message:', normalizedMessage);

                    // ALWAYS update tickets list first (even if ticket not selected)
                    let updatedTicketWithMessage = null;
                    setTickets(prev => {
                        // Update myTickets
                        const updatedMyTickets = prev.myTickets.map(ticket => {
                            if (ticket.ticketId === data.ticketId) {
                                console.log('✅ Found ticket in myTickets, adding message');

                                // Check for duplicates
                                const messageExists = ticket.messages?.some(
                                    msg => msg.messageId === normalizedMessage.messageId
                                );

                                if (messageExists) {
                                    console.log('⚠️ Message already in ticket list, skipping');
                                    updatedTicketWithMessage = ticket; // Still return the ticket for selectedTicket update
                                    return ticket;
                                }

                                // Remove temp message if exists
                                const filteredMessages = (ticket.messages || []).filter(
                                    msg => !(msg.messageId?.startsWith('temp_') && msg.message === normalizedMessage.message)
                                );

                                const updatedMessages = [...filteredMessages, normalizedMessage];
                                // Sort messages by timestamp
                                const sortedMessages = updatedMessages.sort((a, b) => {
                                    const timeA = new Date(a.createdAt || a.time || 0).getTime();
                                    const timeB = new Date(b.createdAt || b.time || 0).getTime();
                                    return timeA - timeB;
                                });

                                console.log('✅ Updated myTickets message count:', sortedMessages.length);
                                updatedTicketWithMessage = { ...ticket, messages: sortedMessages };
                                return updatedTicketWithMessage;
                            }
                            return ticket;
                        });

                        // Also check unassigned tickets (in case admin hasn't accepted yet)
                        const updatedUnassigned = prev.unassignedTickets.map(ticket => {
                            if (ticket.ticketId === data.ticketId) {
                                console.log('✅ Found ticket in unassigned, adding message');
                                const messageExists = ticket.messages?.some(
                                    msg => msg.messageId === normalizedMessage.messageId
                                );

                                if (messageExists) {
                                    if (!updatedTicketWithMessage) {
                                        updatedTicketWithMessage = ticket;
                                    }
                                    return ticket;
                                }

                                const filteredMessages = (ticket.messages || []).filter(
                                    msg => msg.messageId !== normalizedMessage.messageId
                                );
                                const updatedMessages = [...filteredMessages, normalizedMessage];
                                const sortedMessages = updatedMessages.sort((a, b) => {
                                    const timeA = new Date(a.createdAt || a.time || 0).getTime();
                                    const timeB = new Date(b.createdAt || b.time || 0).getTime();
                                    return timeA - timeB;
                                });
                                if (!updatedTicketWithMessage) {
                                    updatedTicketWithMessage = { ...ticket, messages: sortedMessages };
                                }
                                return { ...ticket, messages: sortedMessages };
                            }
                            return ticket;
                        });

                        return {
                            ...prev,
                            myTickets: updatedMyTickets,
                            unassignedTickets: updatedUnassigned
                        };
                    });

                    // Update selected ticket - ALWAYS sync with updated ticket from list if it matches
                    // This ensures selectedTicket is always in sync, even if it was null before
                    setSelectedTicket(prev => {
                        // If selectedTicket is null but we're on a relevant tab, try to restore it
                        if (!prev && updatedTicketWithMessage && (activeTab === 'myTickets' || activeTab === 'unassigned')) {
                            console.log('🔄 Restoring selectedTicket from updated ticket (was null)');
                            return updatedTicketWithMessage;
                        }

                        // If selectedTicket exists and matches, update it
                        if (prev && prev.ticketId === data.ticketId) {
                            console.log('✅ Ticket is selected, updating selected ticket state');

                            // Use the updated ticket from list to ensure consistency
                            if (updatedTicketWithMessage) {
                                console.log('✅ Syncing selectedTicket with updated ticket from list');
                                return updatedTicketWithMessage;
                            }

                            // Fallback: update manually if ticket not found in list update
                            // Check if message already exists by messageId only
                            const messageExists = prev.messages?.some(
                                msg => msg.messageId === normalizedMessage.messageId
                            );

                            if (messageExists) {
                                console.log('⚠️ Message already exists in selected ticket (same messageId), skipping duplicate');
                                return prev;
                            }

                            // Remove temporary message if exists (replace with real one)
                            const filteredMessages = (prev.messages || []).filter(
                                msg => !(msg.messageId?.startsWith('temp_') &&
                                    msg.message === normalizedMessage.message &&
                                    msg.senderRole === normalizedMessage.senderRole)
                            );

                            const updatedMessages = [...filteredMessages, normalizedMessage];
                            // Sort messages by timestamp to ensure correct order
                            const sortedMessages = updatedMessages.sort((a, b) => {
                                const timeA = new Date(a.createdAt || a.time || 0).getTime();
                                const timeB = new Date(b.createdAt || b.time || 0).getTime();
                                return timeA - timeB;
                            });

                            console.log('✅ Updated selected ticket messages count:', sortedMessages.length);
                            console.log('📝 Message added to selectedTicket:', normalizedMessage);
                            return {
                                ...prev,
                                messages: sortedMessages
                            };
                        }

                        // If different ticket selected or no ticket selected
                        if (!prev) {
                            console.log('ℹ️ No ticket selected, message added to ticket list only');
                        } else {
                            console.log('ℹ️ Different ticket selected, message added to ticket list only');
                        }
                        return prev;
                    });

                    console.log('📬 ===== END RECEIVED MESSAGE (ADMIN) =====');
                });

                // Listen for ticket closed
                adminSocketEvents.onTicketClosed((data) => {
                    console.log('🔒 Ticket closed event received:', data);
                    console.log('🔍 Current selected ticket:', selectedTicket?.ticketId);
                    console.log('🔍 Closed ticket ID:', data.ticketId);
                    // Move ticket to closed list
                    setTickets(prev => {
                        const closedTicket = prev.myTickets.find(t => t.ticketId === data.ticketId);
                        console.log('📦 Found closed ticket in myTickets:', closedTicket);
                        const newState = {
                            ...prev,
                            myTickets: prev.myTickets.filter(t => t.ticketId !== data.ticketId),
                            closedTickets: closedTicket
                                ? [{ ...closedTicket, status: 'closed', closedAt: data.closedAt }, ...prev.closedTickets]
                                : prev.closedTickets
                        };
                        console.log('📊 New tickets state:', {
                            myTicketsCount: newState.myTickets.length,
                            closedTicketsCount: newState.closedTickets.length
                        });
                        return newState;
                    });

                    // Update selected ticket status and navigate to closed tab
                    setSelectedTicket(prev => {
                        if (prev?.ticketId === data.ticketId) {
                            console.log('✅ Updating selected ticket status to closed');
                            setActiveTab('closed');
                            return { ...prev, status: 'closed', closedAt: data.closedAt };
                        }
                        return prev;
                    });
                });

                // Listen for ticket reassignment success (for the admin who initiated it)
                adminSocketEvents.onTicketReassignmentSuccess((data) => {
                    console.log('🔄 Ticket reassignment success:', data);
                    const currentAdminId = localStorage.getItem('ID');
                    const reassignedTicketId = data.ticket?.ticketId;

                    if (reassignedTicketId) {
                        // Remove from myTickets for the old admin
                        setTickets(prev => ({
                            ...prev,
                            myTickets: prev.myTickets.filter(t => t.ticketId !== reassignedTicketId)
                        }));

                        // If this was the selected ticket, clear selection
                        if (selectedTicket?.ticketId === reassignedTicketId) {
                            setSelectedTicket(null);
                        }

                    }
                });

                // Listen for ticket assigned success (for the assigner admin)
                adminSocketEvents.onTicketAssignedSuccess((data) => {
                    console.log('✅ Ticket assignment success:', data);
                    toast.success(t('support.ticket_assigned_success', { defaultValue: 'Ticket assigned successfully' }));
                });

                // Listen for ticket reassigned (broadcast to all admins in the room)
                adminSocketEvents.onTicketReassigned((data) => {
                    console.log('🔄 Ticket reassigned event:', data);
                    const currentAdminId = localStorage.getItem('ID');

                    // If the ticket was reassigned TO the current admin, add to myTickets
                    if (data.newAdminId === currentAdminId) {
                        // Request fresh ticket list to get the full ticket object
                        adminSocketEvents.requestTickets();
                    }

                    // If the ticket was reassigned FROM the current admin, remove from myTickets
                    if (data.oldAdminId === currentAdminId) {
                        setTickets(prev => ({
                            ...prev,
                            myTickets: prev.myTickets.filter(t => t.ticketId !== data.ticketId)
                        }));

                        if (selectedTicket?.ticketId === data.ticketId) {
                            setSelectedTicket(null);
                        }
                    }
                });

            } catch (error) {
                console.error('❌ Error setting up listeners:', error);
                toast.warning(t('support.failed_setup_listeners'));
                setIsLoading(false);
                setupInProgress.current = false;
                listenersAttached.current = false;
            }
        };

        // Call the setup function
        setupListenersAndRequestTickets();

        // Cleanup on unmount
        return () => {
            // Clear loading timeout if still pending
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }

            // Reset flags for next mount
            setupInProgress.current = false;
            listenersAttached.current = false;
            socketRetryCount.current = 0;

            // Clean up listeners when component unmounts
            // They will be re-attached when component mounts again
            console.log('🧹 Component unmounting - cleaning up listeners');

            adminSocketEvents.removeListener('unassignedTickets');
            adminSocketEvents.removeListener('adminTickets');
            adminSocketEvents.removeListener('closedTickets');
            adminSocketEvents.removeListener('newSupportTicket');
            adminSocketEvents.removeListener('adminTicketListUpdate');
            adminSocketEvents.removeListener('ticketAccepted');
            adminSocketEvents.removeListener('ticketAlreadyAssigned');
            adminSocketEvents.removeListener('supportTicketJoined');
            adminSocketEvents.removeListener('supportHistory');
            adminSocketEvents.removeListener('receiveSupportMessage');
            adminSocketEvents.removeListener('supportTicketClosed');
            adminSocketEvents.removeListener('ticketReassignmentSuccess');
            adminSocketEvents.removeListener('ticketAssignedSuccess');
            adminSocketEvents.removeListener('ticketReassigned');
        };
    }, [location.key]); // Re-run when navigating to this page

    // Listen for FCM push notifications to refresh ticket lists
    useEffect(() => {
        const handleSupportUpdate = () => {
            console.log('📬 FCM support-ticket-update received, refreshing admin tickets...');
            if (isSupportSocketConnected()) {
                adminSocketEvents.requestTickets();
            }
        };
        window.addEventListener('support-ticket-update', handleSupportUpdate);
        return () => window.removeEventListener('support-ticket-update', handleSupportUpdate);
    }, []);

    // Handle browser tab visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('👁️ Tab became visible - refreshing tickets...');

                // Check if socket is still connected
                if (!isSupportSocketConnected()) {
                    console.warn('⚠️ Socket disconnected while tab was hidden, reconnecting...');
                    const adminId = localStorage.getItem('ID');
                    if (adminId) {
                        try {
                            const socket = connectSupportSocket({ role: 'admin', id: adminId });
                            if (!socket) {
                                console.warn('⚠️ Failed to reconnect socket - will retry automatically');
                            }
                        } catch (error) {
                            console.error('❌ Error reconnecting socket:', error);
                            // Socket.IO will handle reconnection automatically
                        }
                    }
                }

                // Request fresh ticket data
                adminSocketEvents.requestTickets();

                // If a ticket is selected, refresh its data by rejoining
                if (selectedTicket?.ticketId) {
                    const adminId = localStorage.getItem('ID');
                    console.log('🔄 Refreshing selected ticket:', selectedTicket.ticketId);
                    adminSocketEvents.joinTicket(selectedTicket.ticketId, adminId, 'admin');
                }
            }
        };

        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);
        visibilityListenerRef.current = handleVisibilityChange;

        return () => {
            // Remove visibility change listener
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedTicket]); // Re-run when selectedTicket changes

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [selectedTicket?.messages]);

    // Sync selectedTicket with tickets list when messages are updated
    useEffect(() => {
        if (selectedTicket?.ticketId) {
            // Check myTickets first
            let ticketFromList = tickets.myTickets.find(t => t.ticketId === selectedTicket.ticketId);

            // If not found, check unassigned
            if (!ticketFromList) {
                ticketFromList = tickets.unassignedTickets.find(t => t.ticketId === selectedTicket.ticketId);
            }

            // If not found, check closed
            if (!ticketFromList) {
                ticketFromList = tickets.closedTickets.find(t => t.ticketId === selectedTicket.ticketId);
            }

            if (ticketFromList && ticketFromList.messages) {
                const listMessageCount = ticketFromList.messages.length;
                const selectedMessageCount = selectedTicket.messages?.length || 0;

                // If list has more messages, sync them
                if (listMessageCount > selectedMessageCount) {
                    console.log('🔄 Syncing selectedTicket with tickets list (messages updated)');
                    console.log('📊 List has', listMessageCount, 'messages, selected has', selectedMessageCount);
                    setSelectedTicket(prev => ({
                        ...prev,
                        messages: ticketFromList.messages
                    }));
                }
            }
        }
    }, [tickets.myTickets, tickets.unassignedTickets, tickets.closedTickets]);

    // Clear selected ticket if myTickets becomes empty
    useEffect(() => {
        if (activeTab === 'myTickets' && tickets.myTickets.length === 0 && selectedTicket) {
            console.log('📭 My Tickets is empty, clearing selected ticket');
            setSelectedTicket(null);
        }
    }, [tickets.myTickets.length, activeTab, selectedTicket]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 3) {
            toast.warning(t('chat.max_files_limit', 'Maximum 3 files allowed'));
            return;
        }

        const validFiles = files.filter(file => {
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                toast.warning(t('chat.file_too_large', 'File size must be less than 10MB'));
            }
            return isLt10M;
        });

        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDownload = (fileUrl) => {
        if (!fileUrl) return;
        const fileName = fileUrl.split('/').pop();
        const downloadUrl = `${baseURL}/support/message/download/${fileName}`;
        window.open(downloadUrl, '_blank');
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return '';
        }
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('support.date_just_now');
        if (diffMins < 60) return t('support.date_minutes_ago', { minutes: diffMins, plural: diffMins > 1 ? 's' : '' });
        if (diffHours < 24) return t('support.date_hours_ago', { hours: diffHours, plural: diffHours > 1 ? 's' : '' });
        if (diffDays < 7) return t('support.date_days_ago', { days: diffDays, plural: diffDays > 1 ? 's' : '' });
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Admin dropdown search handler
    const handleAdminSearchChange = (e) => {
        const value = e.target.value;
        if (adminSearchError && value.length < adminSearchQuery.length) {
            setAdminSearchError('');
        }
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setAdminSearchError(t('support.search_too_long', { length: MAX_SEARCH_LENGTH, defaultValue: `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters` }));
        } else {
            const finalValue = sanitized.length > MAX_SEARCH_LENGTH
                ? sanitized.substring(0, MAX_SEARCH_LENGTH)
                : sanitized;
            setAdminSearchQuery(finalValue);
            setAdminSearchError('');
        }
    };

    // Toggle admin dropdown for a ticket
    const toggleAdminDropdown = (ticketId) => {
        if (openAdminDropdownId === ticketId) {
            setOpenAdminDropdownId(null);
            setAdminSearchQuery('');
            setAdminSearchError('');
        } else {
            setOpenAdminDropdownId(ticketId);
            setAdminSearchQuery('');
            setAdminSearchError('');
            dispatch(selectAdminList());
        }
    };

    // Select an admin from dropdown — immediately trigger accept with that admin
    const handleSelectAdmin = (ticketId, admin) => {
        setSelectedAdmins((prev) => ({ ...prev, [ticketId]: admin }));
        setOpenAdminDropdownId(null);
        setAdminSearchQuery('');
        setAdminSearchError('');

        // Find the ticket and immediately trigger accept with the selected admin
        const ticket = tickets.unassignedTickets.find(t => t.ticketId === ticketId);
        if (ticket) {
            const currentAdminId = localStorage.getItem('ID');
            const currentAdminName = localStorage.getItem('Name') || 'Admin';
            const isSelf = admin.adminId === currentAdminId;
            const assignAdminId = admin.adminId;
            const assignAdminName = `${admin.name} ${admin.surname || ''}`.trim();

            const confirmText = isSelf
                ? t('support.confirm_accept_text', { defaultValue: 'Are you sure you want to accept this ticket?' })
                : t('support.confirm_assign_text', { defaultValue: `Are you sure you want to assign this ticket to ${assignAdminName}?` });

            // Priority guard: warn (same dialog) if this isn't the top-priority ticket.
            const ordered = prioritized(tickets.unassignedTickets);
            const topTicket = ordered[0];
            const isTop = !topTicket || topTicket.ticketId === ticket.ticketId;
            let priorityHtml = '';
            if (!isTop) {
                const st = slaState(topTicket, Date.now());
                const topLabel = TIER_LABEL[st.tier] || st.tier;
                const topLeft = st.overdue ? `overdue by ${fmtDur(st.msLeft)}` : `${fmtDur(st.msLeft)} left`;
                priorityHtml = `<div style="margin-bottom:10px;padding:10px 12px;background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;color:#b45309;font-size:13px;text-align:left;line-height:1.5;">`
                    + `⚠️ <b>${topTicket.ticketId}</b> (${topLabel} · ${topLeft}) is the current <b>top priority</b>. `
                    + `You're selecting a lower-priority ticket <b>${ticket.ticketId}</b>.</div>`;
            }

            Swal.fire({
                title: !isTop
                    ? t('support.confirm_skip_priority_title', { defaultValue: 'Skip higher priority?' })
                    : isSelf
                        ? t('support.confirm_accept_title', { defaultValue: 'Accept Ticket' })
                        : t('support.confirm_assign_title', { defaultValue: 'Assign Ticket' }),
                html: `${priorityHtml}<div>${confirmText}</div>`,
                icon: !isTop ? 'warning' : 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: !isTop
                    ? t('support.yes_continue_anyway', { defaultValue: 'Continue anyway' })
                    : isSelf
                        ? t('support.yes_accept', { defaultValue: 'Yes, accept it!' })
                        : t('support.yes_assign', { defaultValue: 'Yes, assign it!' }),
                cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
            }).then((result) => {
                if (result.isConfirmed) {
                    if (isSelf) {
                        adminSocketEvents.acceptTicket(ticket.ticketId, assignAdminId, assignAdminName);
                    } else {
                        // Use assignTicket for delegating to another admin
                        adminSocketEvents.assignTicket(ticket.ticketId, assignAdminId, currentAdminId);
                    }
                }
                // Clear selection regardless of confirm/cancel
                setSelectedAdmins((prev) => {
                    const updated = { ...prev };
                    delete updated[ticketId];
                    return updated;
                });
            });
        }
    };

    // Fetch admin list on debounced search
    useEffect(() => {
        if (openAdminDropdownId && !adminSearchError) {
            dispatch(selectAdminList(debouncedAdminSearch));
        }
    }, [debouncedAdminSearch, dispatch, openAdminDropdownId, adminSearchError]);

    // Click-outside to close admin dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target)) {
                setOpenAdminDropdownId(null);
                setAdminSearchQuery('');
                setAdminSearchError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reassign dropdown handlers
    const handleReassignSearchChange = (e) => {
        const value = e.target.value;
        if (reassignSearchError && value.length < reassignSearchQuery.length) {
            setReassignSearchError('');
        }
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setReassignSearchError(t('support.search_too_long', { length: MAX_SEARCH_LENGTH, defaultValue: `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters` }));
        } else {
            setReassignSearchQuery(sanitized);
            setReassignSearchError('');
        }
    };

    const toggleReassignDropdown = () => {
        if (reassignDropdownOpen) {
            setReassignDropdownOpen(false);
            setReassignSearchQuery('');
            setReassignSearchError('');
        } else {
            setReassignDropdownOpen(true);
            setReassignSearchQuery('');
            setReassignSearchError('');
            dispatch(selectAdminList());
        }
    };

    // Fetch admin list on debounced reassign search
    useEffect(() => {
        if (reassignDropdownOpen && !reassignSearchError) {
            dispatch(selectAdminList(debouncedReassignSearch));
        }
    }, [debouncedReassignSearch, dispatch, reassignDropdownOpen, reassignSearchError]);

    // Click-outside to close reassign dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (reassignDropdownRef.current && !reassignDropdownRef.current.contains(e.target)) {
                setReassignDropdownOpen(false);
                setReassignSearchQuery('');
                setReassignSearchError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle reassign admin selection — show SweetAlert then emit socket
    const handleReassignAdmin = (admin) => {
        setReassignDropdownOpen(false);
        setReassignSearchQuery('');
        setReassignSearchError('');

        if (!selectedTicket) return;

        const currentAdminId = localStorage.getItem('ID');
        const currentAdminName = localStorage.getItem('Name') || 'Admin';
        const newAdminName = `${admin.name} ${admin.surname || ''}`.trim();

        if (admin.adminId === currentAdminId) {
            return;
        }

        Swal.fire({
            title: t('support.confirm_reassign_title', { defaultValue: 'Reassign Ticket' }),
            text: t('support.confirm_reassign_text', { defaultValue: `Are you sure you want to reassign this ticket to ${newAdminName}?` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('support.yes_reassign', { defaultValue: 'Yes, reassign it!' }),
            cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
        }).then((result) => {
            if (result.isConfirmed) {
                adminSocketEvents.reassignTicket(
                    selectedTicket.ticketId,
                    admin.adminId,
                    newAdminName,
                    currentAdminName
                );
            }
        });
    };

    const handleAcceptTicket = (ticket) => {
        const currentAdminId = localStorage.getItem('ID');
        const currentAdminName = localStorage.getItem('Name') || 'Admin';

        // Check if another admin is selected for this ticket
        const selectedAdmin = selectedAdmins[ticket.ticketId];
        const assignAdminId = selectedAdmin ? selectedAdmin.adminId : currentAdminId;
        const assignAdminName = selectedAdmin ? selectedAdmin.name : currentAdminName;
        const isSelf = !selectedAdmin || selectedAdmin.adminId === currentAdminId;

        const confirmText = isSelf
            ? t('support.confirm_accept_text', { defaultValue: 'Are you sure you want to accept this ticket?' })
            : t('support.confirm_assign_text', { defaultValue: `Are you sure you want to assign this ticket to ${assignAdminName}?` });

        const confirmBtnText = isSelf
            ? t('support.yes_accept', { defaultValue: 'Yes, accept it!' })
            : t('support.yes_assign', { defaultValue: 'Yes, assign it!' });

        // Priority guard: if this isn't the current top-priority unassigned ticket,
        // warn (in the same dialog) that a higher-priority one is waiting.
        const ordered = prioritized(tickets.unassignedTickets);
        const topTicket = ordered[0];
        const isTop = !topTicket || topTicket.ticketId === ticket.ticketId;
        let priorityHtml = '';
        if (!isTop) {
            const st = slaState(topTicket, Date.now());
            const topLabel = TIER_LABEL[st.tier] || st.tier;
            const topLeft = st.overdue ? `overdue by ${fmtDur(st.msLeft)}` : `${fmtDur(st.msLeft)} left`;
            priorityHtml = `<div style="margin-bottom:10px;padding:10px 12px;background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;color:#b45309;font-size:13px;text-align:left;line-height:1.5;">`
                + `⚠️ <b>${topTicket.ticketId}</b> (${topLabel} · ${topLeft}) is the current <b>top priority</b>. `
                + `You're selecting a lower-priority ticket <b>${ticket.ticketId}</b>.</div>`;
        }

        Swal.fire({
            title: !isTop
                ? t('support.confirm_skip_priority_title', { defaultValue: 'Skip higher priority?' })
                : isSelf
                    ? t('support.confirm_accept_title', { defaultValue: 'Accept Ticket' })
                    : t('support.confirm_assign_title', { defaultValue: 'Assign Ticket' }),
            html: `${priorityHtml}<div>${confirmText}</div>`,
            icon: !isTop ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: !isTop
                ? t('support.yes_continue_anyway', { defaultValue: 'Continue anyway' })
                : confirmBtnText,
            cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
        }).then((result) => {
            if (result.isConfirmed) {
                // Emit accept support ticket event via socket
                if (isSelf) {
                    adminSocketEvents.acceptTicket(ticket.ticketId, assignAdminId, assignAdminName);
                } else {
                    // Use assignTicket for delegating to another admin
                    adminSocketEvents.assignTicket(ticket.ticketId, assignAdminId, currentAdminId);
                }

                // Clear selection after accept
                setSelectedAdmins((prev) => {
                    const updated = { ...prev };
                    delete updated[ticket.ticketId];
                    return updated;
                });
            }
        });
    };

    const handleSelectTicket = (ticket) => {
        // Always get the latest ticket from the list to ensure we have the latest messages
        const latestTicket = tickets.myTickets.find(t => t.ticketId === ticket.ticketId) ||
            tickets.unassignedTickets.find(t => t.ticketId === ticket.ticketId) ||
            tickets.closedTickets.find(t => t.ticketId === ticket.ticketId) ||
            ticket;

        console.log('🎫 Selecting ticket (Admin):', latestTicket.ticketId, 'with', latestTicket.messages?.length || 0, 'messages');
        setSelectedTicket(latestTicket);
        // Close reassign dropdown when switching tickets
        setReassignDropdownOpen(false);
        setReassignSearchQuery('');
        setReassignSearchError('');

        const adminId = localStorage.getItem('ID');
        // Join the ticket room to receive messages
        adminSocketEvents.joinTicket(latestTicket.ticketId, adminId, 'admin');
    };

    const scrollToMessage = (msgId) => {
        if (!msgId) return;
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            setTimeout(() => {
                element.classList.remove('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            }, 2000);
        }
    };

    // Maximum length for message input
    const MAX_MESSAGE_LENGTH = 1000;

    // Validate message input
    const validateMessage = (value) => {
        // Sanitize input - remove potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check max length
        if (sanitized.length > MAX_MESSAGE_LENGTH) {
            return t('support.message_too_long', { length: 1000 });
        }

        // Check for whitespace-only (only show error if there's actual input)
        if (value.length > 0 && sanitized.trim() === '') {
            return t('support.message_spaces_only');
        }

        return '';
    };

    // Handle message input change
    const handleMessageChange = (e) => {
        const value = e.target.value;

        // Sanitize input
        let sanitized = value.replace(/[<>]/g, '');

        // Enforce max length
        if (sanitized.length > MAX_MESSAGE_LENGTH) {
            sanitized = sanitized.substring(0, MAX_MESSAGE_LENGTH);
        }

        setMessageInput(sanitized);

        // Validate and set error
        const error = validateMessage(sanitized);
        setMessageError(error);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        // Validate message before sending
        const trimmedMessage = messageInput.trim();
        if (trimmedMessage) {
            const error = validateMessage(trimmedMessage);
            if (error) {
                setMessageError(error);
                return;
            }
        }

        if ((!trimmedMessage && selectedFiles.length === 0) || !selectedTicket || isUploading) return;

        // Verify socket is connected before sending
        if (!isSupportSocketConnected()) {
            console.error('❌ Socket not connected! Cannot send message');
            toast.warning(t('support.not_connected_server'));
            return;
        }

        const adminId = localStorage.getItem('ID');
        const currentFiles = [...selectedFiles];

        if (currentFiles.length > 0) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('ticketId', selectedTicket.ticketId);
                formData.append('senderId', adminId);
                formData.append('senderRole', 'admin');
                formData.append('message', trimmedMessage);
                if (replyToMessage) {
                    formData.append('replyTo', replyToMessage._id);
                }

                currentFiles.forEach(file => {
                    formData.append('files', file);
                });

                await axios.post(`${baseURL}/support/message/send`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                setMessageInput('');
                setSelectedFiles([]);
                setMessageError('');
                setReplyToMessage(null);
            } catch (error) {
                console.error("Error sending support message with files:", error.response?.data || error.message);
                toast.warning(t('support.send_failed', 'Failed to send message'));
            } finally {
                setIsUploading(false);
            }
        } else {
            const messageData = {
                ticketId: selectedTicket.ticketId,
                senderId: adminId,
                senderRole: 'admin',
                message: trimmedMessage
            };
            if (replyToMessage) {
                messageData.replyTo = replyToMessage._id;
            }

            console.log('📤 ADMIN: Sending message:', messageData);
            console.log('🔍 Socket connected:', isSupportSocketConnected());

            // Emit message via socket
            adminSocketEvents.sendMessage(messageData);

            // Optimistic UI update (will be replaced by server response)
            const tempMessageId = `temp_${Date.now()}`;
            const newMessage = {
                messageId: tempMessageId,
                senderRole: 'admin',
                message: trimmedMessage,
                time: new Date().toISOString(),
                createdAt: new Date().toISOString(),
                isRead: false,
                attachments: [],
                replyTo: replyToMessage || null
            };
            setReplyToMessage(null);

            setSelectedTicket(prev => {
                const updatedMessages = [...(prev.messages || []), newMessage];
                // Sort to maintain order
                const sortedMessages = updatedMessages.sort((a, b) => {
                    const timeA = new Date(a.createdAt || a.time || 0).getTime();
                    const timeB = new Date(b.createdAt || b.time || 0).getTime();
                    return timeA - timeB;
                });
                return {
                    ...prev,
                    messages: sortedMessages
                };
            });

            setTickets(prev => ({
                ...prev,
                myTickets: prev.myTickets.map(ticket =>
                    ticket.ticketId === selectedTicket.ticketId
                        ? { ...ticket, messages: [...(ticket.messages || []), newMessage] }
                        : ticket
                )
            }));

            setMessageInput('');
            setMessageError('');
        }
    };

    const handleCloseTicket = async () => {
        if (!selectedTicket) return;

        const result = await Swal.fire({
            title: t('support.close_ticket_confirm'),
            text: t('support.close_ticket_text', 'Are you sure you want to close this ticket? This action cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#6B7280',
            confirmButtonText: t('support.yes_close', 'Yes, close it!'),
            cancelButtonText: t('support.cancel', 'Cancel'),
            reverseButtons: true
        });

        if (result.isConfirmed) {
            const adminId = localStorage.getItem('ID');
            // Emit close ticket event with closedBy param
            adminSocketEvents.closeTicket(selectedTicket.ticketId, adminId);
            // Immediately update selected ticket status for instant UI feedback
            setSelectedTicket(prev => prev ? { ...prev, status: 'closed' } : null);
            setActiveTab('closed');
        }
    };

    const getTabCount = (tab) => {
        if (tab === 'unassigned') return tickets.unassignedTickets.length;
        if (tab === 'myTickets') return tickets.myTickets.length;
        if (tab === 'closed') return tickets.closedTickets.length;
        return 0;
    };

    const getStatusBadge = (status) => {
        const configs = {
            open: { label: t('support.status_open'), color: 'bg-blue-100 text-blue-800', icon: Clock },
            assigned: { label: t('support.status_assigned'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            closed: { label: t('support.status_closed'), color: 'bg-gray-100 text-gray-800', icon: CheckCircle2 }
        };
        const config = configs[status?.toLowerCase()] || configs.open;
        const Icon = config.icon;
        return (
            <span data-testid={`admin-support-status-badge-${status?.toLowerCase() || 'open'}`} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Icon size={12} data-testid={`admin-support-status-icon-${status?.toLowerCase() || 'open'}`} />
                {config.label}
            </span>
        );
    };

    return (
        <>
            <div data-testid="admin-support-container" className="h-full flex flex-col bg-white rounded-2xl shadow-md overflow-hidden">
                {/* Header */}
                <div data-testid="admin-support-header" className="px-6 py-4">
                    <h1 data-testid="admin-support-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('support.support_tickets_title')}
                    </h1>
                    {/* <p className="text-sm text-gray-600 mt-1">{t('support.manage_respond_requests')}</p> */}
                </div>

                {/* Tabs */}
                <div data-testid="admin-support-tabs-container" className="border-b border-gray-200 bg-white">
                    <div className="flex gap-1 px-6">
                        <button
                            data-testid="admin-support-tab-unassigned"
                            onClick={() => {
                                setActiveTab('unassigned');
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'unassigned'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.unassigned_tickets_tab', 'Unassigned Tickets')}
                            {getTabCount('unassigned') > 0 && (<span data-testid="admin-support-tab-unassigned-count" className="ml-2 px-2 py-0.5 bg-[#4ABDBD] text-white text-xs rounded-full">
                                {getTabCount('unassigned')}
                            </span>)}
                        </button>
                        <button
                            data-testid="admin-support-tab-my-tickets"
                            onClick={() => {
                                setActiveTab('myTickets');
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'myTickets'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.my_tickets')}
                            {getTabCount('myTickets') > 0 && (<span data-testid="admin-support-tab-my-tickets-count" className="ml-2 px-2 py-0.5 bg-[#4ABDBD] text-white text-xs rounded-full">
                                {getTabCount('myTickets')}
                            </span>)}
                        </button>
                        <button
                            data-testid="admin-support-tab-closed"
                            onClick={() => {
                                setActiveTab('closed');
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'closed'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.closed_tickets')}
                            {/* {getTabCount('closed') > 0 &&(<span className="ml-2 px-2 py-0.5 bg-gray-400 text-white text-xs rounded-full">
                            {getTabCount('closed')}
                        </span>)} */}
                        </button>
                    </div>
                </div>

                {/* SLA tier sub-tabs (Unassigned / My Tickets) — high priority → low */}
                {(activeTab === 'unassigned' || activeTab === 'myTickets') && (
                    <div data-testid="admin-support-tier-tabs" className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2 border-b border-gray-100 bg-gray-50/60">
                        {SLA_TABS.map((tab) => {
                            const list = activeTab === 'unassigned' ? tickets.unassignedTickets : tickets.myTickets;
                            const count = tab.id === 'all' ? list.length : list.filter((tk) => (tk.supportTier || '48h') === tab.id).length;
                            const on = tierFilter === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    data-testid={`admin-support-tier-tab-${tab.id}`}
                                    onClick={() => setTierFilter(tab.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? 'bg-[#4ABDBD] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                >
                                    {tab.label} <span className="opacity-70">({count})</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Content Area */}
                <div data-testid="admin-support-content-area" className="flex-1 flex overflow-hidden">
                    {/* TAB 1: Unassigned Tickets */}
                    {activeTab === 'unassigned' && (
                        <div data-testid="admin-support-unassigned-section" className="flex-1 overflow-y-auto p-6 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                            <div className="w-full">
                                <h2 data-testid="admin-support-unassigned-heading" className="text-lg font-semibold text-gray-800 mb-4">
                                    {t('support.unassigned_tickets')} ({prioritized(tickets.unassignedTickets).length})
                                </h2>
                                {isLoading ? (
                                    <div data-testid="admin-support-loading-container" className="flex items-center justify-center py-12">
                                        <Loader2 data-testid="admin-support-loading-icon" className="animate-spin text-[#4ABDBD]" size={32} />
                                        <span data-testid="admin-support-loading-text" className="ml-3 text-gray-600">{t('support.loading_tickets')}</span>
                                    </div>
                                ) : prioritized(tickets.unassignedTickets).length === 0 ? (
                                    <div data-testid="admin-support-no-unassigned-container" className="text-center py-12 text-gray-500">
                                        <CheckCircle2 data-testid="admin-support-no-unassigned-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                        <p data-testid="admin-support-no-unassigned-text">{t('support.no_unassigned_tickets')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="admin-support-unassigned-list" className="space-y-4">
                                        {prioritized(tickets.unassignedTickets).map((ticket) => (
                                            <div
                                                data-testid={`admin-support-unassigned-card-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                className="p-4 border-2 border-gray-200 rounded-xl hover:border-[#4ABDBD] transition-all bg-white"
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span data-testid={`admin-support-unassigned-ticket-id-${ticket.ticketId}`} className="font-bold text-gray-800">{ticket.ticketId}</span>
                                                            {getStatusBadge(ticket.status)}
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                                                            <span data-testid={`admin-support-unassigned-user-name-${ticket.ticketId}`} className="flex items-center gap-1">
                                                                <User size={14} />
                                                                {ticket.userName || t('support.unknown_user')}
                                                            </span>
                                                            <span data-testid={`admin-support-unassigned-user-role-${ticket.ticketId}`} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                                                                {ticket.userRole}
                                                            </span>
                                                            {ticket.userId && (
                                                                <span data-testid={`admin-support-unassigned-user-id-${ticket.ticketId}`} className="px-2 py-0.5 bg-[#4ABDBD]/10 text-[#3A9B9B] rounded-full text-xs font-mono">
                                                                    {ticket.userId}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                                            {(ticket.category || ticket.subCategory || ticket.categoryId || ticket.subCategoryId) && (
                                                                <span data-testid={`admin-support-unassigned-category-${ticket.ticketId}`} className="flex items-center gap-1">
                                                                    <Tag size={14} />
                                                                    <TicketCategoryLine ticket={ticket} />
                                                                </span>
                                                            )}
                                                            <span data-testid={`admin-support-unassigned-date-${ticket.ticketId}`} className="flex items-center gap-1">
                                                                <Calendar size={14} />
                                                                {formatDate(ticket.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-4">
                                                        <SlaRing ticket={ticket} />
                                                        {/* Admin Select Dropdown */}
                                                        <div ref={openAdminDropdownId === ticket.ticketId ? adminDropdownRef : null} className="relative w-52">
                                                            <button
                                                                data-testid={`admin-support-admin-dropdown-button-${ticket.ticketId}`}
                                                                onClick={() => toggleAdminDropdown(ticket.ticketId)}
                                                                disabled={adminListLoading && openAdminDropdownId === ticket.ticketId}
                                                                className={`w-full px-3 py-2 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                                    ${adminListLoading && openAdminDropdownId === ticket.ticketId
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : selectedAdmins[ticket.ticketId]
                                                                            ? 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'
                                                                            : 'text-gray-700 bg-white border-gray-300 cursor-pointer hover:border-[#4ABDBD]'
                                                                    }`}
                                                            >
                                                                <span className="truncate w-[85%] text-left">
                                                                    {selectedAdmins[ticket.ticketId]
                                                                        ? `${selectedAdmins[ticket.ticketId].name} ${selectedAdmins[ticket.ticketId].surname || ''} (${selectedAdmins[ticket.ticketId].adminId})`
                                                                        : t('support.select_admin', { defaultValue: 'Select Admin' })}
                                                                </span>
                                                                <span className="ml-1 flex items-center">
                                                                    {adminListLoading && openAdminDropdownId === ticket.ticketId ? (
                                                                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                                                    ) : openAdminDropdownId === ticket.ticketId ? (
                                                                        <ChevronUp size={16} />
                                                                    ) : (
                                                                        <ChevronDown size={16} />
                                                                    )}
                                                                </span>
                                                            </button>

                                                            {openAdminDropdownId === ticket.ticketId && (
                                                                <div data-testid={`admin-support-admin-dropdown-${ticket.ticketId}`} className="absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                                    {/* Search Input */}
                                                                    <div className="p-2 border-b border-gray-200">
                                                                        <div className="relative">
                                                                            <Search data-testid="admin-support-admin-search-icon" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                                                            <input
                                                                                data-testid="admin-support-admin-search-input"
                                                                                ref={adminSearchInputRef}
                                                                                type="text"
                                                                                value={adminSearchQuery}
                                                                                onChange={handleAdminSearchChange}
                                                                                maxLength={50}
                                                                                placeholder={t('support.search_admin', { defaultValue: 'Search by name, email, phone...' })}
                                                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${adminSearchError
                                                                                    ? 'border-red-300 bg-red-50 focus:ring-red-500'
                                                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                                                    }`}
                                                                                autoFocus
                                                                            />
                                                                        </div>
                                                                        {adminSearchError && (
                                                                            <p data-testid="admin-support-admin-search-error" className="text-xs text-red-600 mt-1 px-1">{adminSearchError}</p>
                                                                        )}
                                                                    </div>

                                                                    {/* Loading Spinner */}
                                                                    {adminListLoading && (
                                                                        <div data-testid="admin-support-admin-list-loading" className="p-4 flex justify-center">
                                                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                        </div>
                                                                    )}

                                                                    {/* Admin List — exclude the currently logged-in admin (self).
                                                                        The "Accept" button already self-assigns; this dropdown
                                                                        is only for delegating to OTHER admins. */}
                                                                    {!adminListLoading && (() => {
                                                                        const currentAdminId = localStorage.getItem('ID');
                                                                        const selectableAdmins = (adminList || []).filter(
                                                                            (admin) => admin.adminId !== currentAdminId
                                                                        );
                                                                        return (
                                                                            <ul data-testid="admin-support-admin-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                                                                                {selectableAdmins.length === 0 ? (
                                                                                    <li data-testid="admin-support-no-admins-found" className="px-4 py-3 text-gray-500 text-center text-sm">
                                                                                        {adminSearchQuery
                                                                                            ? t('support.no_admins_found', { defaultValue: 'No admins found' })
                                                                                            : t('support.no_admins_available', { defaultValue: 'No admins available' })}
                                                                                    </li>
                                                                                ) : (
                                                                                    selectableAdmins.map((admin, idx) => (
                                                                                        <li
                                                                                            data-testid={`admin-support-admin-option-${admin.adminId}`}
                                                                                            key={admin.adminId}
                                                                                            onClick={() => handleSelectAdmin(ticket.ticketId, admin)}
                                                                                            className={`px-4 py-2.5 transition flex justify-between items-center cursor-pointer hover:bg-[#4ABDBD] hover:text-white text-sm
                                                                                                ${idx !== selectableAdmins.length - 1 ? 'border-b border-gray-100' : ''}
                                                                                                ${selectedAdmins[ticket.ticketId]?.adminId === admin.adminId ? 'bg-[#4ABDBD]/10' : ''}`}
                                                                                        >
                                                                                            <span>{admin.name} {admin.surname} ({admin.adminId})</span>
                                                                                        </li>
                                                                                    ))
                                                                                )}
                                                                            </ul>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Accept Button */}
                                                        <button
                                                            data-testid={`admin-support-accept-button-${ticket.ticketId}`}
                                                            onClick={() => handleAcceptTicket(ticket)}
                                                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium text-sm whitespace-nowrap"
                                                        >
                                                            {t('support.accept')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 2: My Tickets */}
                    {activeTab === 'myTickets' && (
                        <div data-testid="admin-support-my-tickets-section" className="flex-1 flex overflow-hidden">
                            {/* Ticket List */}
                            <div data-testid="admin-support-my-tickets-list-container" className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                <div className="p-4 border-b border-gray-200 bg-white">
                                    <h3 data-testid="admin-support-my-tickets-heading" className="font-semibold text-gray-800">{t('support.my_tickets')} ({prioritized(tickets.myTickets).length})</h3>
                                </div>
                                {prioritized(tickets.myTickets).length === 0 ? (
                                    <div data-testid="admin-support-no-my-tickets-container" className="p-4 text-center text-gray-500 text-sm">
                                        <p data-testid="admin-support-no-my-tickets-text">{t('support.no_tickets_assigned')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="admin-support-my-tickets-list" className="divide-y divide-gray-200">
                                        {prioritized(tickets.myTickets).map((ticket) => (
                                            <button
                                                data-testid={`admin-support-my-ticket-item-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                onClick={() => handleSelectTicket(ticket)}
                                                className={`w-full p-4 text-left hover:bg-gray-100 transition-colors ${selectedTicket?.ticketId === ticket.ticketId
                                                    ? 'bg-[#4ABDBD]/10 border-l-4 border-[#4ABDBD]'
                                                    : 'bg-white'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <span data-testid={`admin-support-my-ticket-id-${ticket.ticketId}`} className="font-semibold text-gray-800 text-sm">{ticket.ticketId}</span>
                                                    <div className="flex items-center gap-2">
                                                        {getStatusBadge(ticket.status)}
                                                        <SlaRing ticket={ticket} size={34} stroke={3} />
                                                    </div>
                                                </div>
                                                <p data-testid={`admin-support-my-ticket-user-${ticket.ticketId}`} className="text-xs text-gray-600 mb-1">
                                                    {ticket.userName || t('support.unknown_user')}
                                                    {ticket.userId && (
                                                        <span data-testid={`admin-support-my-ticket-user-id-${ticket.ticketId}`} className="ml-2 px-1.5 py-0.5 bg-[#4ABDBD]/10 text-[#3A9B9B] rounded-full text-[10px] font-mono">
                                                            {ticket.userId}
                                                        </span>
                                                    )}
                                                </p>
                                                {ticket.subject && (
                                                    <p data-testid={`admin-support-my-ticket-subject-${ticket.ticketId}`} className="text-xs text-gray-700 font-medium mb-1">
                                                        <TicketSubjectLabel ticket={ticket} />
                                                    </p>
                                                )}
                                                {(ticket.category || ticket.subCategory || ticket.categoryId || ticket.subCategoryId) && (
                                                    <p data-testid={`admin-support-my-ticket-category-${ticket.ticketId}`} className="text-xs text-gray-500">
                                                        <TicketCategoryLine ticket={ticket} />
                                                    </p>
                                                )}
                                                <p data-testid={`admin-support-my-ticket-date-${ticket.ticketId}`} className="text-xs text-gray-400 mt-2">{formatDate(ticket.createdAt)}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Chat Area */}
                            <div data-testid="admin-support-chat-area" className="flex-1 flex flex-col bg-white">
                                {selectedTicket ? (
                                    <>
                                        {/* Chat Header */}
                                        <div data-testid="admin-support-chat-header" className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h3 data-testid="admin-support-chat-ticket-id" className="font-semibold text-gray-800">{selectedTicket.ticketId}</h3>
                                                    <p data-testid="admin-support-chat-user-info" className="text-sm text-gray-600">
                                                        {selectedTicket.userName || t('support.unknown_user')} ({selectedTicket.userRole})
                                                        {selectedTicket.userId && (
                                                            <span data-testid="admin-support-chat-user-id" className="ml-2 px-2 py-0.5 bg-[#4ABDBD]/10 text-[#3A9B9B] rounded-full text-xs font-mono">
                                                                {selectedTicket.userId}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {selectedTicket.subject && (
                                                        <p data-testid="admin-support-chat-subject" className="text-xs text-gray-700 mt-1 font-medium">
                                                            {t('support.subject_label')}{' '}
                                                            <TicketSubjectLabel ticket={selectedTicket} />
                                                        </p>
                                                    )}
                                                    {(selectedTicket.category || selectedTicket.subCategory || selectedTicket.categoryId || selectedTicket.subCategoryId) && (
                                                        <p data-testid="admin-support-chat-category" className="text-xs text-gray-500 mt-0.5">
                                                            <TicketCategoryLine ticket={selectedTicket} />
                                                        </p>
                                                    )}
                                                </div>
                                                {selectedTicket.status !== 'closed' && (
                                                    <div className="flex items-center gap-3">
                                                        <SlaRing ticket={selectedTicket} size={52} stroke={5} />
                                                        {getStatusBadge(selectedTicket.status)}

                                                        {/* Reassign Admin Dropdown */}
                                                        <div ref={reassignDropdownRef} className="relative w-52">
                                                            <button
                                                                data-testid="admin-support-reassign-dropdown-button"
                                                                onClick={toggleReassignDropdown}
                                                                disabled={adminListLoading && reassignDropdownOpen}
                                                                className={`w-full px-3 py-2 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                                    ${adminListLoading && reassignDropdownOpen
                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                        : 'text-gray-700 bg-white border-gray-300 cursor-pointer hover:border-[#4ABDBD]'
                                                                    }`}
                                                            >
                                                                <span className="truncate w-[85%] text-left">
                                                                    {t('support.reassign_to', { defaultValue: 'Reassign to...' })}
                                                                </span>
                                                                <span className="ml-1 flex items-center">
                                                                    {adminListLoading && reassignDropdownOpen ? (
                                                                        <span className="h-4 w-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                    ) : reassignDropdownOpen ? (
                                                                        <ChevronUp size={16} />
                                                                    ) : (
                                                                        <ChevronDown size={16} />
                                                                    )}
                                                                </span>
                                                            </button>

                                                            {reassignDropdownOpen && (
                                                                <div data-testid="admin-support-reassign-dropdown" className="absolute right-0 mt-2 w-64 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                                    {/* Search Input */}
                                                                    <div className="p-2 border-b border-gray-200">
                                                                        <div className="relative">
                                                                            <Search data-testid="admin-support-reassign-search-icon" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                                                            <input
                                                                                data-testid="admin-support-reassign-search-input"
                                                                                type="text"
                                                                                value={reassignSearchQuery}
                                                                                onChange={handleReassignSearchChange}
                                                                                maxLength={50}
                                                                                placeholder={t('support.search_admin', { defaultValue: 'Search by name, email, phone...' })}
                                                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${reassignSearchError
                                                                                    ? 'border-red-300 bg-red-50 focus:ring-red-500'
                                                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                                                    }`}
                                                                                autoFocus
                                                                            />
                                                                        </div>
                                                                        {reassignSearchError && (
                                                                            <p data-testid="admin-support-reassign-search-error" className="text-xs text-red-600 mt-1 px-1">{reassignSearchError}</p>
                                                                        )}
                                                                    </div>

                                                                    {/* Loading Spinner */}
                                                                    {adminListLoading && (
                                                                        <div data-testid="admin-support-reassign-list-loading" className="p-4 flex justify-center">
                                                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                        </div>
                                                                    )}

                                                                    {/* Admin List — exclude the currently logged-in admin (self).
                                                                        Reassigning to yourself is a no-op, so hide self from the list. */}
                                                                    {!adminListLoading && (() => {
                                                                        const currentAdminId = localStorage.getItem('ID');
                                                                        const reassignableAdmins = (adminList || []).filter(
                                                                            (admin) => admin.adminId !== currentAdminId
                                                                        );
                                                                        return (
                                                                            <ul data-testid="admin-support-reassign-admin-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                                                                                {reassignableAdmins.length === 0 ? (
                                                                                    <li data-testid="admin-support-reassign-no-admins" className="px-4 py-3 text-gray-500 text-center text-sm">
                                                                                        {reassignSearchQuery
                                                                                            ? t('support.no_admins_found', { defaultValue: 'No admins found' })
                                                                                            : t('support.no_admins_available', { defaultValue: 'No admins available' })}
                                                                                    </li>
                                                                                ) : (
                                                                                    reassignableAdmins.map((admin, idx) => (
                                                                                        <li
                                                                                            data-testid={`admin-support-reassign-option-${admin.adminId}`}
                                                                                            key={admin.adminId}
                                                                                            onClick={() => handleReassignAdmin(admin)}
                                                                                            className={`px-4 py-2.5 transition flex justify-between items-center cursor-pointer hover:bg-[#4ABDBD] hover:text-white text-sm
                                                                                                ${idx !== reassignableAdmins.length - 1 ? 'border-b border-gray-100' : ''}`}
                                                                                        >
                                                                                            <span>{admin.name} {admin.surname} ({admin.adminId})</span>
                                                                                        </li>
                                                                                    ))
                                                                                )}
                                                                            </ul>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <button
                                                            data-testid="admin-support-close-ticket-button"
                                                            onClick={handleCloseTicket}
                                                            className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all text-sm whitespace-nowrap"
                                                        >
                                                            {t('support.close_ticket')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div data-testid="admin-support-messages-section" className="flex-1 relative min-h-0 flex flex-col">
                                            <div
                                                data-testid="admin-support-messages-container"
                                                ref={chatAreaRef}
                                                className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400"
                                                onScroll={handleScroll}
                                            >
                                                {selectedTicket.messages?.map((message, index) => {
                                                    const isAdmin = message.senderRole === 'admin';
                                                    const isBot = message.senderRole === 'bot';
                                                    const isUser = message.senderRole === 'doctor' || message.senderRole === 'patient';
                                                    const isSystem = message.senderRole === 'system';

                                                    // System banners (assignment / reassignment). Render as a centered
                                                    // pill with role-specific copy so the current admin sees who handed
                                                    // the ticket over to them.
                                                    if (isSystem) {
                                                        const evt = message.systemEvent || {};
                                                        const isAssignEvt = evt.type === 'assigned';
                                                        const isReassignEvt = evt.type === 'reassigned';
                                                        return (
                                                            <div
                                                                data-testid={`admin-support-system-message-${index}`}
                                                                id={`message-${message.messageId}`}
                                                                key={index}
                                                                className="flex justify-center my-3"
                                                            >
                                                                <div className="max-w-[85%] px-4 py-2 rounded-full bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border border-[#4ABDBD]/30 text-center">
                                                                    {isAssignEvt && (
                                                                        <p data-testid={`admin-support-system-assign-${index}`} className="text-xs text-gray-700">
                                                                            <span className="font-semibold text-[#2D8A8A]">
                                                                                {t('support.ticket_assigned_banner', { defaultValue: 'Ticket assigned' })}
                                                                            </span>
                                                                            <span className="mx-1">·</span>
                                                                            {t('support.assigned_by_prefix', { defaultValue: 'by' })}{' '}
                                                                            <span className="font-semibold">{evt.assignedByAdminName || t('support.sender_admin', 'Admin')}</span>
                                                                            {evt.toAdminName && (
                                                                                <>
                                                                                    {' '}
                                                                                    {t('support.assigned_to_prefix', { defaultValue: 'to' })}{' '}
                                                                                    <span className="font-semibold">{evt.toAdminName}</span>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    )}
                                                                    {isReassignEvt && (
                                                                        <p data-testid={`admin-support-system-reassign-${index}`} className="text-xs text-gray-700">
                                                                            <span className="font-semibold text-[#2D8A8A]">
                                                                                {t('support.ticket_reassigned_banner', { defaultValue: 'Ticket reassigned' })}
                                                                            </span>
                                                                            <span className="mx-1">·</span>
                                                                            {t('support.previously_handled_by', { defaultValue: 'Previously handled by' })}{' '}
                                                                            <span className="font-semibold">{evt.fromAdminName || t('support.another_admin', 'another admin')}</span>
                                                                            {evt.toAdminName && (
                                                                                <>
                                                                                    {' '}
                                                                                    →{' '}
                                                                                    <span className="font-semibold">{evt.toAdminName}</span>
                                                                                </>
                                                                            )}
                                                                            <span className="block text-[11px] text-gray-500 mt-0.5">
                                                                                {t('support.continue_from_here', { defaultValue: 'Please continue from here.' })}
                                                                            </span>
                                                                        </p>
                                                                    )}
                                                                    {!isAssignEvt && !isReassignEvt && (
                                                                        <p data-testid={`admin-support-system-text-${index}`} className="text-xs text-gray-600">{message.message}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Get sender display name - Priority: message.senderName from backend
                                                    let senderName = message.senderName;
                                                    if (!senderName) {
                                                        if (isBot) senderName = t('support.sender_support_bot', 'Support Bot');
                                                        else if (isAdmin) senderName = t('support.sender_you', 'You');
                                                        else if (message.senderRole === 'doctor') senderName = `${selectedTicket.userName || t('support.sender_doctor', 'Doctor')}`;
                                                        else if (message.senderRole === 'patient') senderName = `${selectedTicket.userName || t('support.sender_patient', 'Patient')}`;
                                                        else senderName = message.senderRole || t('support.sender_user', 'User');
                                                    }

                                                    return (
                                                        <div
                                                            data-testid={`admin-support-message-row-${index}`}
                                                            id={`message-${message.messageId}`}
                                                            key={index}
                                                            className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-3 group/msg py-1.5`}
                                                        >
                                                            <div className={`flex flex-col ${isAdmin ? 'items-end' : 'items-start'} max-w-[70%] relative`}>
                                                                {/* Sender Name (only for received messages) */}
                                                                {!isAdmin && (
                                                                    <span data-testid={`admin-support-message-sender-${index}`} className="text-xs font-bold text-[#4ABDBD] mb-1 px-2">
                                                                        {senderName}
                                                                    </span>
                                                                )}

                                                                {/* Message Bubble */}
                                                                <div
                                                                    data-testid={`admin-support-message-bubble-${index}`}
                                                                    className={`rounded-2xl px-4 py-2 shadow-sm relative transition-all duration-300 ${isAdmin
                                                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                                                                        : isBot
                                                                            ? 'bg-blue-50 text-blue-900 border border-blue-200 rounded-bl-md'
                                                                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                                                                        }`}
                                                                >
                                                                    {/* Reply To Reference inside bubble */}
                                                                    {message.replyTo && typeof message.replyTo === 'object' && (
                                                                        <div
                                                                            data-testid={`admin-support-message-reply-ref-${index}`}
                                                                            onClick={() => scrollToMessage(message.replyTo.messageId || message.replyTo._id)}
                                                                            className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isAdmin ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                                                                }`}>
                                                                            <div className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                                                                <Reply size={10} />
                                                                                {message.replyTo.senderRole === 'admin' ? t('support.sender_admin', 'Admin') : t('support.sender_user', 'User')}
                                                                            </div>
                                                                            <div className="line-clamp-2 truncate">{message.replyTo.message || t('support.attachment', 'Attachment')}</div>
                                                                        </div>
                                                                    )}
                                                                    <p data-testid={`admin-support-message-text-${index}`} className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>

                                                                    {/* Attachments */}
                                                                    {message.attachments && message.attachments.length > 0 && (
                                                                        <div data-testid={`admin-support-message-attachments-${index}`} className="mt-3 grid gap-2 min-w-[200px]">
                                                                            {message.attachments.map((file, i) => (
                                                                                <div data-testid={`admin-support-message-attachment-${index}-${i}`} key={i} className={`rounded-xl overflow-hidden border ${isAdmin ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}>
                                                                                    {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                                                                        <div data-testid={`admin-support-attachment-image-${index}-${i}`} className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))}>
                                                                                            <img
                                                                                                data-testid={`admin-support-attachment-img-${index}-${i}`}
                                                                                                src={getFileURL(file.url)}
                                                                                                alt={file.fileName || t('support.image_alt', 'Image')}
                                                                                                className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                                            />
                                                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                                                <button data-testid={`admin-support-attachment-view-button-${index}-${i}`} className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100">
                                                                                                    <Eye size={20} />
                                                                                                </button>
                                                                                                <button
                                                                                                    data-testid={`admin-support-attachment-download-button-${index}-${i}`}
                                                                                                    className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleDownload(file.url);
                                                                                                    }}
                                                                                                >
                                                                                                    <Download size={20} />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                                                                        <div data-testid={`admin-support-attachment-voice-${index}-${i}`} className={`p-3 min-w-[240px] ${isAdmin ? 'bg-white/10' : 'bg-[#4ABDBD]/5'} rounded-xl`}>
                                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                                <Volume2 size={16} className={isAdmin ? 'text-white' : 'text-[#4ABDBD]'} />
                                                                                                <span data-testid={`admin-support-attachment-voice-label-${index}-${i}`} className={`text-xs font-bold ${isAdmin ? 'text-white' : 'text-gray-700'}`}>
                                                                                                    {t('chat.voice_message', 'Voice Message')}
                                                                                                </span>
                                                                                            </div>
                                                                                            <audio
                                                                                                data-testid={`admin-support-attachment-audio-${index}-${i}`}
                                                                                                src={getFileURL(file.url)}
                                                                                                controls
                                                                                                className={`w-[255px] h-12 custom-audio-player ${isAdmin ? 'invert brightness-200' : ''}`}
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div data-testid={`admin-support-attachment-file-${index}-${i}`} className="p-3 flex items-center gap-3">
                                                                                            <div className={`p-2 rounded-lg ${isAdmin ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                                                <File size={20} />
                                                                                            </div>
                                                                                            <div className="flex-1 min-w-0">
                                                                                                <p data-testid={`admin-support-attachment-filename-${index}-${i}`} className="text-xs font-bold truncate">{file.fileName}</p>
                                                                                                <p data-testid={`admin-support-attachment-mimetype-${index}-${i}`} className="text-[10px] opacity-60 uppercase">{file.mimeType ? file.mimeType.split('/')[1] : 'FILE'}</p>
                                                                                            </div>
                                                                                            <button
                                                                                                data-testid={`admin-support-attachment-file-download-button-${index}-${i}`}
                                                                                                className={`p-2 rounded-lg transition-colors ${isAdmin ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                                                onClick={() => handleDownload(file.url)}
                                                                                            >
                                                                                                <Download size={18} />
                                                                                            </button>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    <p data-testid={`admin-support-message-time-${index}`} className={`text-xs mt-1.5 ${isAdmin ? 'text-white/70' : 'text-gray-500'
                                                                        }`}>
                                                                        {formatTime(message.createdAt || message.time)}
                                                                    </p>
                                                                </div>

                                                                {/* Reply Button (Hover) */}
                                                                {message.senderRole !== 'bot' && (
                                                                    <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 z-10 ${isAdmin ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'}`}>
                                                                        <button
                                                                            data-testid={`admin-support-message-reply-button-${index}`}
                                                                            onClick={() => setReplyToMessage(message)}
                                                                            className="p-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-full shadow-sm transition-all"
                                                                            title={t('support.reply', 'Reply')}
                                                                        >
                                                                            <Reply size={14} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div ref={messagesEndRef} />
                                            </div>
                                            <AnimatePresence>
                                                {showScrollBottom && (
                                                    <motion.button
                                                        data-testid="admin-support-scroll-bottom-button"
                                                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                                                        onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                                        className="absolute bottom-4 right-6 p-3 bg-[#4ABDBD] text-white rounded-full shadow-lg hover:bg-[#3A9B9B] transition-all z-10 flex items-center justify-center group"
                                                        title={t('chat.recent_messages', 'Recent messages')}
                                                    >
                                                        <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform" />
                                                    </motion.button>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        {/* Input Area */}
                                        <div data-testid="admin-support-input-area" className="p-4 border-t border-gray-200 bg-white relative">
                                            {/* Reply to preview in input box area */}
                                            {replyToMessage && (
                                                <div data-testid="admin-support-reply-preview" className="mb-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 flex items-start justify-between shadow-sm animate-in slide-in-from-bottom-2">
                                                    <div className="flex-1 min-w-0 pr-4">
                                                        <div data-testid="admin-support-reply-preview-label" className="flex items-center gap-1 text-xs font-semibold text-[#4ABDBD] mb-1">
                                                            <Reply size={12} />
                                                            {t('support.replying_to', 'Replying to')} {replyToMessage.senderRole === 'admin' ? t('support.sender_you', 'You') : t('support.sender_user', 'User')}
                                                        </div>
                                                        <p data-testid="admin-support-reply-preview-text" className="text-sm text-gray-600 truncate">{replyToMessage.message || t('support.attachment', 'Attachment')}</p>
                                                    </div>
                                                    <button
                                                        data-testid="admin-support-reply-preview-close-button"
                                                        onClick={() => setReplyToMessage(null)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            )}

                                            {/* File Previews */}
                                            {selectedFiles.length > 0 && (
                                                <div data-testid="admin-support-file-previews" className="mb-3 flex flex-wrap gap-2 animate-in slide-in-from-bottom-2">
                                                    {selectedFiles.map((file, index) => (
                                                        <div data-testid={`admin-support-file-preview-${index}`} key={index} className="relative group bg-gray-50 border border-gray-200 rounded-xl p-2 pr-9 flex items-center gap-2 shadow-sm">
                                                            {file.type.startsWith('image/') ? (
                                                                <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center overflow-hidden">
                                                                    <ImageIcon data-testid={`admin-support-file-preview-image-icon-${index}`} size={16} className="text-[#4ABDBD]" />
                                                                </div>
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                                                                    <File data-testid={`admin-support-file-preview-file-icon-${index}`} size={16} className="text-gray-500" />
                                                                </div>
                                                            )}
                                                            <span data-testid={`admin-support-file-preview-name-${index}`} className="text-[11px] font-bold text-gray-700 truncate max-w-[100px]">{file.name}</span>
                                                            <button
                                                                data-testid={`admin-support-file-preview-remove-button-${index}`}
                                                                type="button"
                                                                onClick={() => removeFile(index)}
                                                                className="absolute right-1 text-gray-400 hover:text-red-500 p-1 transition-colors"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <form data-testid="admin-support-message-form" onSubmit={handleSendMessage} className="flex flex-col gap-2">
                                                <div className="flex gap-2 items-center">
                                                    <input
                                                        data-testid="admin-support-file-input"
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileChange}
                                                        multiple
                                                        className="hidden"
                                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                                    />
                                                    <button
                                                        data-testid="admin-support-attach-file-button"
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="p-2 text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 rounded-xl transition-all border border-gray-200"
                                                        title={t('chat.attach_files', 'Attach Files')}
                                                    >
                                                        <Paperclip size={20} />
                                                    </button>

                                                    <input
                                                        data-testid="admin-support-message-input"
                                                        type="text"
                                                        value={messageInput}
                                                        onChange={handleMessageChange}
                                                        maxLength={1000}
                                                        disabled={isUploading}
                                                        placeholder={t('support.type_message_placeholder')}
                                                        className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${messageError
                                                            ? 'border-red-300 bg-red-50 focus:ring-red-500'
                                                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                            }`}
                                                    />
                                                    {messageInput.trim() || selectedFiles.length > 0 ? (
                                                        <button
                                                            data-testid="admin-support-send-message-button"
                                                            type="submit"
                                                            disabled={isUploading}
                                                            className={`px-6 h-[42px] flex items-center justify-center bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                                                        </button>
                                                    ) : (
                                                        <VoiceRecorder
                                                            isUploading={isUploading}
                                                            disabled={isUploading}
                                                            onRecordingComplete={(file) => {
                                                                const formData = new FormData();
                                                                const adminId = localStorage.getItem('ID');
                                                                formData.append('ticketId', selectedTicket.ticketId);
                                                                formData.append('senderId', adminId);
                                                                formData.append('senderRole', 'admin');
                                                                formData.append('files', file);

                                                                setIsUploading(true);
                                                                axios.post(`${baseURL}/support/message/send`, formData, {
                                                                    headers: { 'Content-Type': 'multipart/form-data' },
                                                                }).then(() => {
                                                                    // Handle success
                                                                }).catch(err => {
                                                                    console.error("Error sending voice note:", err);
                                                                    toast.warning(t('support.send_failed'));
                                                                }).finally(() => {
                                                                    setIsUploading(false);
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {messageError && (
                                                    <p data-testid="admin-support-message-error" className="text-xs text-red-600">{messageError}</p>
                                                )}
                                            </form>
                                        </div>
                                    </>
                                ) : (
                                    <div data-testid="admin-support-no-ticket-selected-container" className="flex-1 flex items-center justify-center text-gray-500">
                                        <div className="text-center">
                                            <MessageCircle data-testid="admin-support-no-ticket-selected-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                            <p data-testid="admin-support-no-ticket-selected-text">{t('support.select_ticket_message')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 3: Closed Tickets */}
                    {activeTab === 'closed' && (
                        <div data-testid="admin-support-closed-section" className="flex-1 flex overflow-hidden">
                            {/* Ticket List */}
                            <div data-testid="admin-support-closed-list-container" className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                <div className="p-4 border-b border-gray-200 bg-white">
                                    <h3 data-testid="admin-support-closed-heading" className="font-semibold text-gray-800">{t('support.closed_tickets')} ({tickets.closedTickets.length})</h3>
                                </div>
                                {tickets.closedTickets.length === 0 ? (
                                    <div data-testid="admin-support-no-closed-container" className="p-4 text-center text-gray-500 text-sm">
                                        <p data-testid="admin-support-no-closed-text">{t('support.no_closed_tickets', 'No closed tickets.')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="admin-support-closed-list" className="divide-y divide-gray-200">
                                        {tickets.closedTickets.map((ticket) => (
                                            <button
                                                data-testid={`admin-support-closed-ticket-item-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                onClick={() => handleSelectTicket(ticket)}
                                                className={`w-full p-4 text-left hover:bg-gray-100 transition-colors ${selectedTicket?.ticketId === ticket.ticketId
                                                    ? 'bg-[#4ABDBD]/10 border-l-4 border-[#4ABDBD]'
                                                    : 'bg-white'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <span data-testid={`admin-support-closed-ticket-id-${ticket.ticketId}`} className="font-semibold text-gray-800 text-sm">{ticket.ticketId}</span>
                                                    {getStatusBadge(ticket.status)}
                                                </div>
                                                <p data-testid={`admin-support-closed-ticket-user-${ticket.ticketId}`} className="text-xs text-gray-600 mb-1">
                                                    {ticket.userName || t('support.unknown_user')}
                                                    {ticket.userId && (
                                                        <span data-testid={`admin-support-closed-ticket-user-id-${ticket.ticketId}`} className="ml-2 px-1.5 py-0.5 bg-[#4ABDBD]/10 text-[#3A9B9B] rounded-full text-[10px] font-mono">
                                                            {ticket.userId}
                                                        </span>
                                                    )}
                                                </p>
                                                {ticket.subject && (
                                                    <p data-testid={`admin-support-closed-ticket-subject-${ticket.ticketId}`} className="text-xs text-gray-700 font-medium mb-1">
                                                        <TicketSubjectLabel ticket={ticket} />
                                                    </p>
                                                )}
                                                {(ticket.category || ticket.subCategory || ticket.categoryId || ticket.subCategoryId) && (
                                                    <p data-testid={`admin-support-closed-ticket-category-${ticket.ticketId}`} className="text-xs text-gray-500">
                                                        <TicketCategoryLine ticket={ticket} />
                                                    </p>
                                                )}
                                                <p data-testid={`admin-support-closed-ticket-date-${ticket.ticketId}`} className="text-xs text-gray-400 mt-2">{t('support.closed_prefix')} {formatDate(ticket.closedAt)}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Chat Area (Read-only) */}
                            <div data-testid="admin-support-closed-chat-area" className="flex-1 flex flex-col bg-white">
                                {selectedTicket ? (
                                    <>
                                        {/* Chat Header */}
                                        <div data-testid="admin-support-closed-chat-header" className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 data-testid="admin-support-closed-chat-ticket-id" className="font-semibold text-gray-800">{selectedTicket.ticketId}</h3>
                                                        <span data-testid="admin-support-closed-chat-status-label" className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                                                            {t('support.ticket_closed')}
                                                        </span>
                                                    </div>
                                                    <p data-testid="admin-support-closed-chat-user-info" className="text-sm text-gray-600">
                                                        {selectedTicket.userName || t('support.unknown_user')} ({selectedTicket.userRole})
                                                        {selectedTicket.userId && (
                                                            <span data-testid="admin-support-closed-chat-user-id" className="ml-2 px-2 py-0.5 bg-[#4ABDBD]/10 text-[#3A9B9B] rounded-full text-xs font-mono">
                                                                {selectedTicket.userId}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {selectedTicket.subject && (
                                                        <p data-testid="admin-support-closed-chat-subject" className="text-xs text-gray-700 mt-1 font-medium">
                                                            {t('support.subject_label')}{' '}
                                                            <TicketSubjectLabel ticket={selectedTicket} />
                                                        </p>
                                                    )}
                                                    {(selectedTicket.category || selectedTicket.subCategory || selectedTicket.categoryId || selectedTicket.subCategoryId) && (
                                                        <p data-testid="admin-support-closed-chat-category" className="text-xs text-gray-500 mt-0.5">
                                                            <TicketCategoryLine ticket={selectedTicket} />
                                                        </p>
                                                    )}
                                                </div>
                                                {getStatusBadge(selectedTicket.status)}
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div data-testid="admin-support-closed-messages-container" ref={chatAreaRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                            {selectedTicket.messages?.map((message, index) => {
                                                const isAdmin = message.senderRole === 'admin';
                                                const isBot = message.senderRole === 'bot';
                                                const isUser = message.senderRole === 'doctor' || message.senderRole === 'patient';
                                                const isSystem = message.senderRole === 'system';

                                                // System banners (assignment / reassignment) — centered pill.
                                                if (isSystem) {
                                                    const evt = message.systemEvent || {};
                                                    const isAssignEvt = evt.type === 'assigned';
                                                    const isReassignEvt = evt.type === 'reassigned';
                                                    return (
                                                        <div
                                                            data-testid={`admin-support-closed-system-message-${index}`}
                                                            id={`message-${message.messageId}`}
                                                            key={index}
                                                            className="flex justify-center my-3"
                                                        >
                                                            <div className="max-w-[85%] px-4 py-2 rounded-full bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border border-[#4ABDBD]/30 text-center">
                                                                {isAssignEvt && (
                                                                    <p data-testid={`admin-support-closed-system-assign-${index}`} className="text-xs text-gray-700">
                                                                        <span className="font-semibold text-[#2D8A8A]">
                                                                            {t('support.ticket_assigned_banner', { defaultValue: 'Ticket assigned' })}
                                                                        </span>
                                                                        <span className="mx-1">·</span>
                                                                        {t('support.assigned_by_prefix', { defaultValue: 'by' })}{' '}
                                                                        <span className="font-semibold">{evt.assignedByAdminName || t('support.sender_admin', 'Admin')}</span>
                                                                        {evt.toAdminName && (
                                                                            <>
                                                                                {' '}
                                                                                {t('support.assigned_to_prefix', { defaultValue: 'to' })}{' '}
                                                                                <span className="font-semibold">{evt.toAdminName}</span>
                                                                            </>
                                                                        )}
                                                                    </p>
                                                                )}
                                                                {isReassignEvt && (
                                                                    <p data-testid={`admin-support-closed-system-reassign-${index}`} className="text-xs text-gray-700">
                                                                        <span className="font-semibold text-[#2D8A8A]">
                                                                            {t('support.ticket_reassigned_banner', { defaultValue: 'Ticket reassigned' })}
                                                                        </span>
                                                                        <span className="mx-1">·</span>
                                                                        {t('support.previously_handled_by', { defaultValue: 'Previously handled by' })}{' '}
                                                                        <span className="font-semibold">{evt.fromAdminName || t('support.another_admin', 'another admin')}</span>
                                                                        {evt.toAdminName && (
                                                                            <>
                                                                                {' '}
                                                                                →{' '}
                                                                                <span className="font-semibold">{evt.toAdminName}</span>
                                                                            </>
                                                                        )}
                                                                        <span className="block text-[11px] text-gray-500 mt-0.5">
                                                                            {t('support.continue_from_here', { defaultValue: 'Please continue from here.' })}
                                                                        </span>
                                                                    </p>
                                                                )}
                                                                {!isAssignEvt && !isReassignEvt && (
                                                                    <p data-testid={`admin-support-closed-system-text-${index}`} className="text-xs text-gray-600">{message.message}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // Get sender display name - Priority: message.senderName from backend
                                                let senderName = message.senderName;
                                                if (!senderName) {
                                                    if (isBot) senderName = t('support.sender_support_bot', 'Support Bot');
                                                    else if (isAdmin) senderName = t('support.sender_you', 'You');
                                                    else if (message.senderRole === 'doctor') senderName = t('support.sender_doctor', 'Doctor');
                                                    else if (message.senderRole === 'patient') senderName = t('support.sender_patient', 'Patient');
                                                    else senderName = message.senderRole || t('support.sender_user', 'User');
                                                }

                                                return (
                                                    <div
                                                        data-testid={`admin-support-closed-message-row-${index}`}
                                                        id={`message-${message.messageId}`}
                                                        key={index}
                                                        className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-3 group/msg py-1.5`}
                                                    >
                                                        <div className={`flex flex-col ${isAdmin ? 'items-end' : 'items-start'} max-w-[70%] relative`}>
                                                            {/* Sender Name (only for received messages) */}
                                                            {!isAdmin && (
                                                                <span data-testid={`admin-support-closed-message-sender-${index}`} className="text-xs font-bold text-[#4ABDBD] mb-1 px-2">
                                                                    {senderName}
                                                                </span>
                                                            )}

                                                            {/* Message Bubble */}
                                                            <div
                                                                data-testid={`admin-support-closed-message-bubble-${index}`}
                                                                className={`rounded-2xl px-4 py-2 shadow-sm relative transition-all duration-300 ${isAdmin
                                                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                                                                    : isBot
                                                                        ? 'bg-blue-50 text-blue-900 border border-blue-200 rounded-bl-md'
                                                                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                                                                    }`}
                                                            >
                                                                {/* Reply To Reference inside bubble */}
                                                                {message.replyTo && typeof message.replyTo === 'object' && (
                                                                    <div
                                                                        data-testid={`admin-support-closed-message-reply-ref-${index}`}
                                                                        onClick={() => scrollToMessage(message.replyTo.messageId || message.replyTo._id)}
                                                                        className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isAdmin ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                                                            }`}
                                                                    >
                                                                        <div className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                                                            <Reply size={10} />
                                                                            {message.replyTo.senderRole === 'admin' ? t('support.sender_admin', 'Admin') : t('support.sender_user', 'User')}
                                                                        </div>
                                                                        <div className="line-clamp-2 truncate">{message.replyTo.message || t('support.attachment', 'Attachment')}</div>
                                                                    </div>
                                                                )}
                                                                <p data-testid={`admin-support-closed-message-text-${index}`} className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>

                                                                {/* Attachments */}
                                                                {message.attachments && message.attachments.length > 0 && (
                                                                    <div data-testid={`admin-support-closed-message-attachments-${index}`} className="mt-3 grid gap-2 min-w-[200px]">
                                                                        {message.attachments.map((file, i) => (
                                                                            <div data-testid={`admin-support-closed-message-attachment-${index}-${i}`} key={i} className={`rounded-xl overflow-hidden border ${isAdmin ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}>
                                                                                {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                                                                    <div data-testid={`admin-support-closed-attachment-image-${index}-${i}`} className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))}>
                                                                                        <img
                                                                                            data-testid={`admin-support-closed-attachment-img-${index}-${i}`}
                                                                                            src={getFileURL(file.url)}
                                                                                            alt={file.fileName || t('support.image_alt', 'Image')}
                                                                                            className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                                        />
                                                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                                            <button data-testid={`admin-support-closed-attachment-view-button-${index}-${i}`} className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100">
                                                                                                <Eye size={20} />
                                                                                            </button>
                                                                                            <button
                                                                                                data-testid={`admin-support-closed-attachment-download-button-${index}-${i}`}
                                                                                                className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleDownload(file.url);
                                                                                                }}
                                                                                            >
                                                                                                <Download size={20} />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div data-testid={`admin-support-closed-attachment-file-${index}-${i}`} className="p-3 flex items-center gap-3">
                                                                                        <div className={`p-2 rounded-lg ${isAdmin ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                                            <File size={20} />
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p data-testid={`admin-support-closed-attachment-filename-${index}-${i}`} className="text-xs font-bold truncate">{file.fileName}</p>
                                                                                            <p data-testid={`admin-support-closed-attachment-mimetype-${index}-${i}`} className="text-[10px] opacity-60 uppercase">{file.mimeType ? file.mimeType.split('/')[1] : 'FILE'}</p>
                                                                                        </div>
                                                                                        <button
                                                                                            data-testid={`admin-support-closed-attachment-file-download-button-${index}-${i}`}
                                                                                            className={`p-2 rounded-lg transition-colors ${isAdmin ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                                            onClick={() => handleDownload(file.url)}
                                                                                        >
                                                                                            <Download size={18} />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                <p data-testid={`admin-support-closed-message-time-${index}`} className={`text-xs mt-1.5 ${isAdmin ? 'text-white/70' : 'text-gray-500'
                                                                    }`}>
                                                                    {formatTime(message.createdAt || message.time)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </div>

                                        {/* Disabled Input Area */}
                                        {/* <div className="p-4 border-t border-gray-200 bg-gray-100">
                                        <div className="flex gap-2 opacity-50">
                                            <input
                                                type="text"
                                                disabled
                                                placeholder={t('support.ticket_is_closed')}
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-200 cursor-not-allowed"
                                            />
                                            <button
                                                disabled
                                                className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                                            >
                                                <Send size={20} />
                                            </button>
                                        </div>
                                    </div> */}
                                    </>
                                ) : (
                                    <div data-testid="admin-support-no-closed-selected-container" className="flex-1 flex items-center justify-center text-gray-500">
                                        <div className="text-center">
                                            <MessageCircle data-testid="admin-support-no-closed-selected-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                            <p data-testid="admin-support-no-closed-selected-text">{t('support.select_closed_ticket')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImage && (
                    <motion.div
                        data-testid="admin-support-image-preview-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <motion.div
                            data-testid="admin-support-image-preview-modal-content"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                data-testid="admin-support-image-preview-modal-close-button"
                                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                                onClick={() => setPreviewImage(null)}
                            >
                                <X size={24} />
                            </button>
                            <img
                                data-testid="admin-support-image-preview-modal-img"
                                src={previewImage}
                                alt={t('chat.preview', 'Preview')}
                                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                            />
                            <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                                <button
                                    data-testid="admin-support-image-preview-download-button"
                                    className="px-6 py-2 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                                    onClick={() => handleDownload(previewImage)}
                                >
                                    <Download size={18} />
                                    {t('chat.download', 'Download')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AdminSupport;

